// src/pages/MandateDetail.js
import { useState, useRef, useEffect } from 'react'
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query'
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd'
import * as XLSX from 'xlsx'
import DOMPurify from 'dompurify'
import api from '../lib/api'
import WorkTypeTags from '../components/WorkTypeTags'
import { WORK_TYPE_TREE } from '../lib/workTypes'
import MandateTaxonomyTags from '../components/MandateTaxonomyTags'
import { RGA_PARTNER, PARTNER_FIRM, isRgaPartner, isPartnerFirm } from '../lib/roles'
import { PIPELINE_STAGES, PL_BADGE, PL_ACCENT } from '../lib/pipelineStages'
import ActionItemsList from '../components/ActionItemsList'
import { EntitySearch, EntityField, EntityMultiField } from '../components/EntityPicker'
import MaterialsSection, { SlotCard } from '../components/MaterialsSection'
import { useMandateFacts, FactRows, EditFactsModal, EconomicsAccessModal } from '../components/KeyMetrics'
import { useSearchKeywords, SearchKeywordsLabel, SearchKeywordChips, EditSearchKeywordsModal } from '../components/SearchKeywords'
import { PeekLink } from '../components/Peek'
import { FirmLines } from '../lib/contactFirms'
import { PARTNER_STAGES, PARTNER_BADGE } from '../lib/partnerPipelineStages'
import { nameTypeLabel, BUSINESS_DEVELOPMENT, CFC_FIELD_DEFS, cfcSummary } from '../lib/nameTypes'

// ── Constants ─────────────────────────────────────────────────────────────

const DOCUMENT_TYPES = [
  'Engagement Letter', 'PPM / Offering Document', 'Due Diligence',
  'Meeting Deck', 'Investor Correspondence', 'Other',
]
const ACCEPTED_EXTENSIONS = '.pdf,.docx,.pptx,.xlsx,.doc,.xls,.ppt,.csv,.txt,.jpg,.jpeg,.png'
const MAX_FILE_BYTES = 50 * 1024 * 1024

// ── Google Drive Picker loader ──────────────────────────────────────────
// The Picker script is injected once and cached across the whole app —
// repeated calls to loadGooglePicker() after the first just reuse the same
// promise instead of re-adding the <script> tag.
let _pickerLoadPromise = null
function loadGooglePicker() {
  if (_pickerLoadPromise) return _pickerLoadPromise
  _pickerLoadPromise = new Promise((resolve, reject) => {
    if (window.google?.picker) { resolve(); return }
    const script = document.createElement('script')
    script.src = 'https://apis.google.com/js/api.js'
    script.async = true
    script.onload = () => {
      window.gapi.load('picker', { callback: resolve, onerror: reject })
    }
    script.onerror = () => reject(new Error('Failed to load Google Picker script'))
    document.body.appendChild(script)
  })
  return _pickerLoadPromise
}

// Opens the Drive file picker using a short-lived access token from our own
// backend (GET /api/gmail/drive-token) — the user never sees a second
// Google login, since this reuses the Gmail/Calendar connection's token.
async function openDrivePicker({ accessToken, onPicked }) {
  await loadGooglePicker()
  const apiKey = process.env.REACT_APP_GOOGLE_API_KEY
  if (!apiKey) throw new Error('REACT_APP_GOOGLE_API_KEY is not configured for this app.')

  const view = new window.google.picker.DocsView()
    .setIncludeFolders(true)
    .setSelectFolderEnabled(false)

  const picker = new window.google.picker.PickerBuilder()
    .addView(view)
    .setOAuthToken(accessToken)
    .setDeveloperKey(apiKey)
    .setCallback((data) => {
      if (data.action === window.google.picker.Action.PICKED) {
        const doc = data.docs[0]
        onPicked({ id: doc.id, name: doc.name })
      }
    })
    .build()

  picker.setVisible(true)
}

const BD_STAGES    = ['REVIEWING', 'IN DISCUSSIONS', 'NEGOTIATING EL', 'LIVE', 'ON HOLD', 'ON THE RADAR', 'CLOSED', 'PASSED']
const INT_TYPES    = ['Email', 'Meeting', 'Call', 'Note', 'Event']
const INT_SOURCES  = ['Manual', 'Gmail Sync', 'Calendar Sync', 'Outlook Sync']
const DIRECTIONS   = ['Inbound', 'Outbound']

const BD_BADGE = {
  'REVIEWING': 'badge-gray', 'IN DISCUSSIONS': 'badge-blue',
  'NEGOTIATING EL': 'badge-yellow', 'LIVE': 'badge-green',
  'ON HOLD': 'badge-yellow', 'ON THE RADAR': 'badge-gray',
  'CLOSED': 'badge-gray', 'PASSED': 'badge-red',
}
const TYPE_BADGE = {
  'Email': 'badge-blue', 'Meeting': 'badge-purple',
  'Call': 'badge-green', 'Note': 'badge-gray', 'Event': 'badge-yellow',
}
const DOC_TYPE_BADGE = {
  'Engagement Letter': 'badge-blue', 'PPM / Offering Document': 'badge-purple',
  'Due Diligence': 'badge-yellow', 'Meeting Deck': 'badge-green',
  'Investor Correspondence': 'badge-blue', 'Other': 'badge-gray',
}

// ── Helpers ───────────────────────────────────────────────────────────────

function formatRaise(n) {
  if (n == null) return null
  if (n >= 1e9) return `$${(n / 1e9).toFixed(1)}B`
  if (n >= 1e6) return `$${Math.round(n / 1e6)}M`
  return `$${Number(n).toLocaleString()}`
}
function formatDate(iso) {
  if (!iso) return '—'
  // A bare YYYY-MM-DD (date columns) is read as local, not UTC midnight,
  // which would show the day before in US time zones.
  const d = /^\d{4}-\d{2}-\d{2}$/.test(iso) ? new Date(iso + 'T00:00:00') : new Date(iso)
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}
function formatDateTime(iso) {
  if (!iso) return '—'
  const d = new Date(iso)
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) +
    ' · ' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
}
function formatFileSize(bytes) {
  if (!bytes) return '—'
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  return `${Math.round(bytes / 1024)} KB`
}

function daysSince(iso) {
  if (!iso) return null
  const diff = Math.floor((Date.now() - new Date(iso)) / 86_400_000)
  if (diff === 0) return 'Today'
  if (diff === 1) return '1 day ago'
  return `${diff} days ago`
}

// Shared style for inline inputs/selects — used by PipelineTableView's cells
// and ContactTypeahead below.
const editInputStyle = {
  background: 'transparent', border: 'none', outline: 'none',
  color: 'var(--text-primary)', fontSize: 13, fontFamily: 'inherit',
  width: '100%', padding: 0, minWidth: 80,
}

// ── StageMenu (reusable inline dropdown) ─────────────────────────────────

// ── Partner Pipeline Section ──────────────────────────────────────────────
// Which RGA Partners are being worked on this mandate
// and how far along each is. Separate from portal engagement
// (mandate_partners): reaching Partnered here grants no portal access.

function PartnerPipelineSection({ mandateId }) {
  const qc = useQueryClient()
  const [adding, setAdding]       = useState(false)
  const [stageFor, setStageFor]   = useState(null)   // row id whose stage menu is open
  const [err, setErr]             = useState(null)
  const key = ['partner-pipeline', mandateId]

  const { data: rows = [], isLoading } = useQuery({
    queryKey: key,
    queryFn: () => api.get(`/api/mandates/${mandateId}/partner-pipeline`).then(r => r.data.data ?? []),
  })

  const { data: partners = [] } = useQuery({
    queryKey: ['partners'],
    queryFn: () => api.get('/api/partners').then(r => r.data.data ?? []),
    enabled: adding,
  })

  const onError = e => setErr(e.response?.data?.error || e.message)
  const refresh = () => { setErr(null); qc.invalidateQueries({ queryKey: key }) }

  const addMutation = useMutation({
    mutationFn: partner_contact_id => api.post(`/api/mandates/${mandateId}/partner-pipeline`, { partner_contact_id }),
    onSuccess: refresh, onError,
  })
  const stageMutation = useMutation({
    mutationFn: ({ id, stage }) => api.patch(`/api/partner-pipeline/${id}`, { stage }),
    onSuccess: refresh, onError,
  })
  const removeMutation = useMutation({
    mutationFn: id => api.delete(`/api/partner-pipeline/${id}`),
    onSuccess: refresh, onError,
  })
  const agreementMutation = useMutation({
    mutationFn: ({ id, onFile }) => api.patch(`/api/partner-pipeline/${id}`, { partnership_agreement_on_file: onFile }),
    onSuccess: refresh, onError,
  })

  const inPipeline = new Set(rows.map(r => r.partner_contact_id))
  const available  = partners.filter(p => !inPipeline.has(p.contact_id))
  const counts     = PARTNER_STAGES.map(s => [s, rows.filter(r => r.stage === s).length]).filter(([, n]) => n)

  function handleRemove(row) {
    if (!window.confirm(`Remove ${row.partner?.name ?? 'this partner'} from this mandate's partner pipeline?`)) return
    removeMutation.mutate(row.id)
  }

  return (
    <div className="card" style={{ marginBottom: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: rows.length > 0 ? 14 : 0 }}>
        <div>
          <h2 style={{ fontSize: 14, fontWeight: 600 }}>Partner Pipeline</h2>
          {!isLoading && (
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>
              {rows.length} partner{rows.length !== 1 ? 's' : ''}
              {counts.length > 0 && ' · ' + counts.map(([s, n]) => `${n} ${s.toLowerCase()}`).join(', ')}
            </div>
          )}
        </div>
        {adding ? (
          <ContactTypeahead
            contacts={available}
            placeholder="Search partners…"
            clearLabel="Cancel"
            createDefaults={{ contact_type: [RGA_PARTNER] }}
            onSave={id => { setAdding(false); if (id) addMutation.mutate(id) }}
            onCancel={() => setAdding(false)}
          />
        ) : (
          <button className="btn btn-primary btn-sm" onClick={() => setAdding(true)} disabled={addMutation.isPending}>
            {addMutation.isPending ? 'Adding…' : '+ Add Partner'}
          </button>
        )}
      </div>

      {err && <ErrorBanner msg={err} />}

      {isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '28px 0' }}>
          <div className="spinner" />
        </div>
      ) : rows.length === 0 ? (
        <div className="empty-state" style={{ padding: '28px 20px', marginTop: 14 }}>
          <h3 style={{ fontSize: 13 }}>No partners yet</h3>
          <p>Add the partners you plan to approach about this mandate.</p>
        </div>
      ) : (
        <div style={{ border: '1px solid var(--border)', borderRadius: 6 }}>
          <div style={{
            display: 'grid', gridTemplateColumns: '1.5fr 150px 190px 1.5fr 90px 36px',
            padding: '7px 14px', background: 'var(--surface-raised)',
            borderBottom: '1px solid var(--border)',
            fontSize: 11, fontWeight: 600, textTransform: 'uppercase',
            letterSpacing: '0.06em', color: 'var(--text-secondary)', gap: 12,
          }}>
            <span>Partner</span>
            <span>Stage</span>
            <span>Agreement</span>
            <span>Firm</span>
            <span>Updated</span>
            <span />
          </div>
          {rows.map((row, idx) => (
            <div key={row.id} style={{
              display: 'grid', gridTemplateColumns: '1.5fr 150px 190px 1.5fr 90px 36px',
              padding: '10px 14px', gap: 12, alignItems: 'center', fontSize: 13,
              borderBottom: idx < rows.length - 1 ? '1px solid var(--border)' : 'none',
            }}>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                <PeekLink type="contact" id={row.partner?.contact_id} style={{ fontWeight: 500 }}>
                  {row.partner?.name ?? 'Unknown contact'}
                </PeekLink>
              </span>
              <div style={{ position: 'relative' }}>
                <button
                  onClick={() => setStageFor(stageFor === row.id ? null : row.id)}
                  className={`badge ${PARTNER_BADGE[row.stage] ?? 'badge-gray'}`}
                  style={{ fontSize: 11, border: 'none', cursor: 'pointer', fontFamily: 'inherit' }}
                  title="Change stage"
                >
                  {row.stage} ▾
                </button>
                {stageFor === row.id && (
                  <StageMenu
                    stages={PARTNER_STAGES}
                    current={row.stage}
                    onSelect={stage => { setStageFor(null); stageMutation.mutate({ id: row.id, stage }) }}
                    onClose={() => setStageFor(null)}
                  />
                )}
              </div>
              <AgreementFlag row={row} pending={agreementMutation.isPending}
                onToggle={onFile => agreementMutation.mutate({ id: row.id, onFile })} />
              <div style={{ color: 'var(--text-secondary)' }}>
                <FirmLines contact={row.partner} />
              </div>
              <span style={{ color: 'var(--text-secondary)' }}>{formatDate(row.updated_at)}</span>
              <button className="btn-icon" onClick={() => handleRemove(row)} disabled={removeMutation.isPending}
                title="Remove from partner pipeline" style={{ color: 'var(--text-muted)', fontSize: 14 }}>
                ✕
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// Partnership agreement per mandate (migrations/084, Travis 10/27): a
// Partnered row without one is flagged until RGA marks it on file. Click to
// toggle. Rows not yet Partnered need no agreement.
function AgreementFlag({ row, pending, onToggle }) {
  if (row.stage !== 'Partnered') return <span style={{ color: 'var(--text-muted)' }}>—</span>
  const onFile = row.partnership_agreement_on_file
  return (
    <span>
      <button
        onClick={() => onToggle(!onFile)}
        disabled={pending}
        className={`badge ${onFile ? 'badge-green' : 'badge-yellow'}`}
        style={{ fontSize: 11, border: 'none', cursor: 'pointer', fontFamily: 'inherit' }}
        title={onFile ? 'Click if the agreement is not on file after all' : 'Click once the signed partnership agreement is on file'}
      >
        {onFile ? 'Agreement on file' : 'No partnership agreement'}
      </button>
    </span>
  )
}

// ── Names submitted by partners (portal Add Names), every status ─────────
// The RGA-side history for this mandate (Travis, 10/21), with who submitted
// and who at RGA handled it. Read-only; names are handled in the dashboard's
// Partner Submissions section. Status wording follows that flow (10/22): RGA
// either adds the name to the pipeline (status approved) or doesn't
// (rejected); the client's view happens later, on the pipeline.
const SUBMISSION_STATUS_BADGE = { pending: 'badge-yellow', approved: 'badge-green', rejected: 'badge-gray' }
const SUBMISSION_STATUS_LABEL = { pending: 'With RGA', approved: 'Added to pipeline', rejected: 'Not approved' }

function SubmittedNamesSection({ mandateId }) {
  const [filter, setFilter] = useState('all')
  const { data: rows = [], isLoading } = useQuery({
    queryKey: ['mandate-submissions', mandateId],
    queryFn: () => api.get(`/api/mandates/${mandateId}/submissions`).then(r => r.data.data ?? []),
  })
  if (isLoading || !rows.length) return null

  const counts = ['pending', 'approved', 'rejected'].map(st => [st, rows.filter(r => r.status === st).length])
  const shown = filter === 'all' ? rows : rows.filter(r => r.status === filter)
  const tab = (value, label) => (
    <button type="button" key={value} onClick={() => setFilter(value)}
      className={`btn btn-sm ${filter === value ? 'btn-primary' : 'btn-ghost'}`}>{label}</button>
  )

  return (
    <div className="card" style={{ marginBottom: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
        <div>
          <h2 style={{ fontSize: 14, fontWeight: 600 }}>Names Submitted by Partners</h2>
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>
            {rows.length} name{rows.length !== 1 ? 's' : ''} · {counts.filter(([, n]) => n).map(([st, n]) => `${n} ${SUBMISSION_STATUS_LABEL[st].toLowerCase()}`).join(', ')}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          {tab('all', 'All')}
          {counts.filter(([, n]) => n).map(([st]) => tab(st, SUBMISSION_STATUS_LABEL[st]))}
        </div>
      </div>
      <div className="table-wrapper">
        <table>
          <thead>
            <tr><th>Name</th><th>Firm or company</th><th>Type</th><th>Submitted by</th><th>Submitted</th><th>Status</th><th>Handled by RGA</th></tr>
          </thead>
          <tbody>
            {shown.map(r => (
              <tr key={r.submission_id}>
                <td>
                  <div style={{ fontWeight: 600 }}>
                    {r.promoted_contact_id ? <PeekLink type="contact" id={r.promoted_contact_id}>{r.contact_name}</PeekLink> : r.contact_name}
                  </div>
                  {r.title_role && <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{r.title_role}</div>}
                  {r.partner_notes && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }} title={r.partner_notes}>
                    {r.partner_notes.length > 90 ? `${r.partner_notes.slice(0, 90)}…` : r.partner_notes}</div>}
                </td>
                <td>{r.firm_name ?? '--'}</td>
                <td>
                  {nameTypeLabel(r.name_type)}
                  {r.name_type === BUSINESS_DEVELOPMENT && (r.cfc_relationship_type || r.cfc_offering) && (
                    <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                      {[r.cfc_relationship_type, r.cfc_offering && `for ${r.cfc_offering}`].filter(Boolean).join(' · ')}
                    </div>
                  )}
                </td>
                <td>{r.partner ? <PeekLink type="contact" id={r.partner.contact_id}>{r.partner.name}</PeekLink> : '--'}</td>
                <td style={{ whiteSpace: 'nowrap' }}>{formatDate(r.created_at)}</td>
                <td><span className={`badge ${SUBMISSION_STATUS_BADGE[r.status] ?? 'badge-gray'}`}>{SUBMISSION_STATUS_LABEL[r.status] ?? r.status}</span></td>
                <td style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                  {r.reviewed_at ? `${formatDate(r.reviewed_at)}${r.reviewer?.name ? `, ${r.reviewer.name}` : ''}` : '--'}
                  {r.review_notes && <div style={{ color: 'var(--text-muted)' }}>{r.review_notes}</div>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ── Strategic advisory and business development pipeline (bones) ──────────
// Rows come from approved partner submissions (migrations/075). Stage and
// the business development fields are edited here; the fields come from
// CFC_FIELD_DEFS so they change in one place. To build out further.
function NamePipelineEditModal({ row, mandateId, onClose, onSaved }) {
  const [form, setForm] = useState(() => ({
    notes: row.notes ?? '',
    ...Object.fromEntries(CFC_FIELD_DEFS.map(f => [f.key, row[f.key] ?? ''])),
  }))
  const [err, setErr] = useState(null)
  const save = useMutation({
    mutationFn: () => api.patch(`/api/mandates/${mandateId}/name-pipeline/${row.name_pipeline_id}`, form),
    onSuccess: () => { onSaved(); onClose() },
    onError: e => setErr(e.response?.data?.error || e.message),
  })
  return (
    <Modal title={`Edit - ${row.contact?.name ?? row.firm?.name ?? 'name'}`} onClose={onClose}>
      <form onSubmit={e => { e.preventDefault(); save.mutate() }} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {row.name_type === BUSINESS_DEVELOPMENT && CFC_FIELD_DEFS.map(f => (
          <Field key={f.key} label={f.label}>
            {f.kind === 'select' ? (
              <select className="select" value={form[f.key]} onChange={e => setForm(v => ({ ...v, [f.key]: e.target.value }))}>
                <option value="">Choose</option>
                {f.options.map(o => <option key={o}>{o}</option>)}
              </select>
            ) : (
              <input className="input" value={form[f.key]} placeholder={f.placeholder}
                onChange={e => setForm(v => ({ ...v, [f.key]: e.target.value }))} />
            )}
          </Field>
        ))}
        <Field label="Notes">
          <textarea className="textarea" rows={4} value={form.notes} onChange={e => setForm(v => ({ ...v, notes: e.target.value }))} />
        </Field>
        {err && <ErrorBanner msg={err} />}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={save.isPending}>{save.isPending ? 'Saving…' : 'Save'}</button>
        </div>
      </form>
    </Modal>
  )
}

function NamePipelineSection({ mandateId }) {
  const qc = useQueryClient()
  const key = ['name-pipeline', mandateId]
  const [editing, setEditing] = useState(null)
  const { data: rows = [], isLoading } = useQuery({
    queryKey: key,
    queryFn: () => api.get(`/api/mandates/${mandateId}/name-pipeline`).then(r => r.data.data ?? []),
  })
  const refresh = () => qc.invalidateQueries({ queryKey: key })
  const stage = useMutation({
    mutationFn: ({ id, stage }) => api.patch(`/api/mandates/${mandateId}/name-pipeline/${id}`, { stage }),
    onSuccess: refresh,
  })
  if (isLoading || !rows.length) return null

  return (
    <div className="card" style={{ marginBottom: 20 }}>
      <h2 style={{ fontSize: 14, fontWeight: 600 }}>Strategic and Business Development Pipeline</h2>
      <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2, marginBottom: 14 }}>
        {rows.length} name{rows.length !== 1 ? 's' : ''} from partner submissions
      </div>
      <div className="table-wrapper">
        <table>
          <thead><tr><th>Name</th><th>Firm or company</th><th>Type</th><th>Partner</th><th>Stage</th><th></th></tr></thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.name_pipeline_id}>
                <td>
                  {r.contact ? <PeekLink type="contact" id={r.contact.contact_id}>{r.contact.name}</PeekLink> : '—'}
                  {r.contact?.title_role && <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{r.contact.title_role}</div>}
                </td>
                <td>{r.firm ? <PeekLink type="firm" id={r.firm.firm_id}>{r.firm.name}</PeekLink> : '—'}</td>
                <td>
                  {nameTypeLabel(r.name_type)}
                  {r.name_type === BUSINESS_DEVELOPMENT && cfcSummary(r) && (
                    <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{cfcSummary(r)}</div>
                  )}
                </td>
                <td>{r.partner_advisor ? <PeekLink type="contact" id={r.partner_advisor.contact_id}>{r.partner_advisor.name}</PeekLink> : '—'}</td>
                <td>
                  <select className="select" style={{ fontSize: 12, padding: '3px 8px', height: 'auto' }} value={r.stage}
                    onChange={e => stage.mutate({ id: r.name_pipeline_id, stage: e.target.value })}>
                    {PIPELINE_STAGES.map(st => <option key={st}>{st}</option>)}
                  </select>
                </td>
                <td><button type="button" className="btn btn-ghost btn-sm" onClick={() => setEditing(r)}>Edit</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editing && <NamePipelineEditModal row={editing} mandateId={mandateId} onClose={() => setEditing(null)} onSaved={refresh} />}
    </div>
  )
}

function StageMenu({ stages, current, onSelect, onClose, zIndex = 200 }) {
  const ref = useRef()
  useEffect(() => {
    const fn = e => { if (ref.current && !ref.current.contains(e.target)) onClose() }
    document.addEventListener('mousedown', fn)
    return () => document.removeEventListener('mousedown', fn)
  }, [onClose])

  return (
    <div ref={ref} style={{
      position: 'absolute', top: 'calc(100% + 4px)', left: 0,
      zIndex, background: 'var(--surface-raised)', border: '1px solid var(--border-light)',
      borderRadius: 6, padding: 4, minWidth: 180,
      boxShadow: '0 6px 20px rgba(0,0,0,0.5)',
    }}>
      {stages.map(s => (
        <button key={s} disabled={s === current}
          onClick={e => { e.stopPropagation(); e.preventDefault(); onSelect(s) }}
          style={{
            display: 'flex', alignItems: 'center', gap: 8, width: '100%',
            background: s === current ? 'var(--accent-muted)' : 'transparent',
            border: 'none', borderRadius: 4, padding: '7px 10px',
            cursor: s === current ? 'default' : 'pointer',
            color: s === current ? 'var(--blue-text)' : 'var(--text-primary)',
            fontSize: 12, fontWeight: s === current ? 600 : 400,
            fontFamily: 'inherit', textAlign: 'left',
          }}>
          <span style={{ width: 12 }}>{s === current ? '✓' : ''}</span>{s}
        </button>
      ))}
    </div>
  )
}

// ── Modal shell ───────────────────────────────────────────────────────────

function Modal({ title, onClose, width = 520, zIndex = 1000, children }) {
  return (
    <div onClick={e => e.target === e.currentTarget && onClose()} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex, padding: 16,
    }}>
      <div style={{
        background: 'var(--surface)', border: '1px solid var(--border-light)',
        borderRadius: 10, width: '100%', maxWidth: width,
        maxHeight: '92vh', overflow: 'auto',
        boxShadow: '0 20px 60px rgba(0,0,0,0.6)',
      }}>
        <div style={{
          padding: '16px 24px', borderBottom: '1px solid var(--border)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          position: 'sticky', top: 0, background: 'var(--surface)', zIndex: 1,
        }}>
          <h2 style={{ fontSize: 15, fontWeight: 600 }}>{title}</h2>
          <button className="btn-icon" onClick={onClose} style={{ fontSize: 16 }}>✕</button>
        </div>
        {children}
      </div>
    </div>
  )
}

function ErrorBanner({ msg }) {
  if (!msg) return null
  return (
    <div style={{
      background: 'var(--danger-muted)', border: '1px solid rgba(239,68,68,0.25)',
      borderRadius: 6, padding: '10px 14px', fontSize: 13, color: 'var(--danger-text)',
    }}>{msg}</div>
  )
}

function TrashIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
      <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z"/>
      <path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"/>
    </svg>
  )
}

function EditIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
      <path d="M12.146.146a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1 0 .708l-10 10a.5.5 0 0 1-.168.11l-5 2a.5.5 0 0 1-.65-.65l2-5a.5.5 0 0 1 .11-.168l10-10zM11.207 2.5 13.5 4.793 14.793 3.5 12.5 1.207 11.207 2.5zm1.586 3L10.5 3.207 4 9.707V10h.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.5h.293l6.5-6.5zm-9.761 5.175-.106.106-1.528 3.821 3.821-1.528.106-.106A.5.5 0 0 1 5 12.5V12h-.5a.5.5 0 0 1-.5-.5V11h-.5a.5.5 0 0 1-.468-.325z"/>
    </svg>
  )
}

function DeleteConfirmModal({ name, onCancel, onConfirm, deleting, error }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1300, padding: 16,
    }}>
      <div style={{
        background: 'var(--surface)', border: '1px solid var(--border-light)',
        borderRadius: 10, width: '100%', maxWidth: 420,
        boxShadow: '0 20px 60px rgba(0,0,0,0.6)', padding: '24px',
      }}>
        <h2 style={{ fontSize: 15, fontWeight: 600, marginBottom: 10 }}>Delete {name}?</h2>
        <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: error ? 14 : 24, lineHeight: 1.5 }}>
          Are you sure you want to delete <strong style={{ color: 'var(--text-primary)' }}>{name}</strong>?
          This action cannot be undone.
        </p>
        {error && <div style={{ marginBottom: 20 }}><ErrorBanner msg={error} /></div>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button className="btn btn-secondary" onClick={onCancel} disabled={deleting}>Cancel</button>
          <button className="btn btn-danger" onClick={onConfirm} disabled={deleting}>
            {deleting ? 'Deleting…' : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  )
}

function Field({ label, children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase',
        letterSpacing: '0.06em', color: 'var(--text-secondary)' }}>
        {label}
      </div>
      <div style={{ fontSize: 13, color: 'var(--text-primary)' }}>{children}</div>
    </div>
  )
}

// ── Signed EL fields ──────────────────────────────────────────────────────
// Shown for Engagement Letters only. A signed EL is what lets a mandate move
// to LIVE (migrations/066); its date fills the mandate's Engagement Letter date.

function SignedElFields({ signed, setSigned, signedDate, setSignedDate, disabled }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
        <input type="checkbox" checked={signed} disabled={disabled}
          onChange={e => setSigned(e.target.checked)}
          style={{ accentColor: 'var(--accent)', cursor: 'pointer' }} />
        Signed / executed
      </label>
      {signed && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <label className="input-label" style={{ margin: 0 }}>Signed on</label>
          <input type="date" className="input" style={{ maxWidth: 160 }} value={signedDate}
            disabled={disabled} onChange={e => setSignedDate(e.target.value)} />
        </div>
      )}
    </div>
  )
}

// ── Upload Document Modal ─────────────────────────────────────────────────

function UploadDocumentModal({ mandateId, onClose, onUploaded, initialType = '' }) {
  const [file,         setFile]     = useState(null)
  const [docType,      setDocType]  = useState(initialType)
  const [notes,        setNotes]    = useState('')
  const [signed,       setSigned]   = useState(false)
  const [signedDate,   setSignedDate] = useState('')
  const [uploading,    setUploading]= useState(false)
  const [progress,     setProgress] = useState(0)
  const [err,          setErr]      = useState(null)
  const [dragging,     setDragging] = useState(false)
  const fileRef = useRef()

  function pickFile(f) {
    if (!f) return
    if (f.size > MAX_FILE_BYTES) { setErr('File must be 50 MB or smaller.'); return }
    setFile(f); setErr(null)
  }

  function handleDrop(e) {
    e.preventDefault(); setDragging(false)
    pickFile(e.dataTransfer.files[0])
  }

  async function handleSubmit(e) {
    e.preventDefault()
    if (!file) return setErr('Please select a file.')
    setUploading(true); setErr(null); setProgress(0)

    const fd = new FormData()
    fd.append('file', file)
    if (docType) fd.append('document_type', docType)
    if (notes)   fd.append('notes', notes)
    if (docType === 'Engagement Letter' && signed) {
      fd.append('is_executed', 'true')
      if (signedDate) fd.append('executed_date', signedDate)
    }

    try {
      await api.post(`/api/mandates/${mandateId}/documents`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: ev => setProgress(Math.round((ev.loaded * 100) / ev.total)),
      })
      onUploaded()
      onClose()
    } catch (e) {
      setErr(e.response?.data?.error || e.message)
    } finally {
      setUploading(false)
    }
  }

  const dropZoneStyle = {
    border: `2px dashed ${dragging ? 'var(--accent)' : 'var(--border-light)'}`,
    borderRadius: 8, padding: '28px 20px', textAlign: 'center', cursor: 'pointer',
    background: dragging ? 'var(--accent-muted)' : 'var(--surface-raised)',
    transition: 'border-color 0.15s, background 0.15s',
  }

  return (
    <Modal title="Upload Document" onClose={onClose} width={480}>
      <form onSubmit={handleSubmit} style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>
        <ErrorBanner msg={err} />

        {/* Drop zone */}
        <div
          style={dropZoneStyle}
          onClick={() => !uploading && fileRef.current?.click()}
          onDragOver={e => { e.preventDefault(); setDragging(true) }}
          onDragLeave={() => setDragging(false)}
          onDrop={handleDrop}
        >
          <input
            ref={fileRef} type="file" accept={ACCEPTED_EXTENSIONS}
            style={{ display: 'none' }}
            onChange={e => pickFile(e.target.files[0])}
          />
          {file ? (
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 4 }}>
                {file.name}
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                {formatFileSize(file.size)} · click to change
              </div>
            </div>
          ) : (
            <div>
              <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 4 }}>
                Drag & drop a file here, or <span style={{ color: 'var(--accent)' }}>browse</span>
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                PDF, Word, Excel, PowerPoint, CSV, TXT, image · max 50 MB
              </div>
            </div>
          )}
        </div>

        {/* Upload progress bar */}
        {uploading && (
          <div style={{ height: 4, borderRadius: 2, background: 'var(--border)', overflow: 'hidden' }}>
            <div style={{
              height: '100%', borderRadius: 2, background: 'var(--accent)',
              width: `${progress}%`, transition: 'width 0.2s',
            }} />
          </div>
        )}

        {/* Document type */}
        <div className="input-group">
          <label className="input-label">Document Type</label>
          <select className="select" value={docType} onChange={e => setDocType(e.target.value)} disabled={uploading}>
            <option value="">Select type…</option>
            {DOCUMENT_TYPES.map(t => <option key={t}>{t}</option>)}
          </select>
        </div>

        {docType === 'Engagement Letter' && (
          <SignedElFields signed={signed} setSigned={setSigned}
            signedDate={signedDate} setSignedDate={setSignedDate} disabled={uploading} />
        )}

        {/* Notes */}
        <div className="input-group">
          <label className="input-label">Notes <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(optional)</span></label>
          <textarea className="textarea" rows={2} value={notes} disabled={uploading}
            placeholder="Version notes, context, what this document covers…"
            onChange={e => setNotes(e.target.value)} />
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, paddingTop: 4,
          borderTop: '1px solid var(--border)', marginTop: 4 }}>
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={uploading}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={uploading || !file}>
            {uploading ? `Uploading ${progress}%…` : 'Upload'}
          </button>
        </div>
      </form>
    </Modal>
  )
}

// ── Drive Link Modal ───────────────────────────────────────────────────────
// Shown right after a file is picked from Google Drive, to collect the same
// document_type/notes metadata the upload flow collects — the file itself
// isn't re-picked here, just linked via drive_file_id.

function DriveLinkModal({ mandateId, driveFile, onClose, onLinked }) {
  const [docType, setDocType] = useState('')
  const [notes,   setNotes]   = useState('')
  const [signed,  setSigned]  = useState(false)
  const [signedDate, setSignedDate] = useState('')
  const [linking, setLinking] = useState(false)
  const [err,     setErr]     = useState(null)

  async function handleSubmit(e) {
    e.preventDefault()
    setLinking(true); setErr(null)
    try {
      await api.post(`/api/mandates/${mandateId}/documents/drive-link`, {
        drive_file_id: driveFile.id,
        document_type: docType || undefined,
        notes: notes || undefined,
        ...(docType === 'Engagement Letter' && signed
          ? { is_executed: true, executed_date: signedDate || undefined }
          : {}),
      })
      onLinked()
      onClose()
    } catch (e) {
      setErr(e.response?.data?.error || e.message)
    } finally {
      setLinking(false)
    }
  }

  return (
    <Modal title="Attach from Google Drive" onClose={onClose} width={480}>
      <form onSubmit={handleSubmit} style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>
        <ErrorBanner msg={err} />

        <div style={{
          border: '1px solid var(--border-light)', borderRadius: 8, padding: '14px 16px',
          background: 'var(--surface-raised)', fontSize: 13, fontWeight: 600,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {driveFile.name}
        </div>

        <div className="input-group">
          <label className="input-label">Document Type</label>
          <select className="select" value={docType} onChange={e => setDocType(e.target.value)} disabled={linking}>
            <option value="">Select type…</option>
            {DOCUMENT_TYPES.map(t => <option key={t}>{t}</option>)}
          </select>
        </div>

        {docType === 'Engagement Letter' && (
          <SignedElFields signed={signed} setSigned={setSigned}
            signedDate={signedDate} setSignedDate={setSignedDate} disabled={linking} />
        )}

        <div className="input-group">
          <label className="input-label">Notes <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(optional)</span></label>
          <textarea className="textarea" rows={2} value={notes} disabled={linking}
            placeholder="Version notes, context, what this document covers…"
            onChange={e => setNotes(e.target.value)} />
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, paddingTop: 4,
          borderTop: '1px solid var(--border)', marginTop: 4 }}>
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={linking}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={linking}>
            {linking ? 'Linking…' : 'Attach'}
          </button>
        </div>
      </form>
    </Modal>
  )
}

// ── Mark Signed Modal ─────────────────────────────────────────────────────
// Marks an Engagement Letter already on file as signed (or clears it).

function MarkSignedModal({ doc, onClose, onSaved }) {
  const [signed,     setSigned]     = useState(!doc.is_executed)
  const [signedDate, setSignedDate] = useState(doc.executed_date ?? '')
  const [saving,     setSaving]     = useState(false)
  const [err,        setErr]        = useState(null)

  async function handleSubmit(e) {
    e.preventDefault()
    setSaving(true); setErr(null)
    try {
      await api.patch(`/api/documents/${doc.document_id}`, {
        is_executed: signed,
        executed_date: signed && signedDate ? signedDate : undefined,
      })
      onSaved()
      onClose()
    } catch (e) {
      setErr(e.response?.data?.error || e.message)
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal title="Engagement Letter" onClose={onClose} width={440}>
      <form onSubmit={handleSubmit} style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>
        <ErrorBanner msg={err} />
        <div style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {doc.file_name}
        </div>
        <SignedElFields signed={signed} setSigned={setSigned}
          signedDate={signedDate} setSignedDate={setSignedDate} disabled={saving} />
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, paddingTop: 4,
          borderTop: '1px solid var(--border)', marginTop: 4 }}>
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </form>
    </Modal>
  )
}

// ── Documents Section ─────────────────────────────────────────────────────

function DocumentsSection({ mandateId }) {
  const [uploadOpen, setUploadOpen] = useState(false)
  const [driveFile,  setDriveFile]  = useState(null)   // picked Drive file awaiting metadata
  const [signingDoc, setSigningDoc] = useState(null)   // EL being marked signed
  const [driveErr,   setDriveErr]   = useState(null)
  const [drivePicking, setDrivePicking] = useState(false)
  const qc = useQueryClient()

  async function handleAttachFromDrive() {
    setDriveErr(null); setDrivePicking(true)
    try {
      const { data } = await api.get('/api/gmail/drive-token')
      const accessToken = data.data?.access_token
      if (!accessToken) throw new Error('No Drive access token returned.')
      await openDrivePicker({ accessToken, onPicked: (file) => setDriveFile(file) })
    } catch (e) {
      setDriveErr(
        e.response?.data?.error || e.message ||
        'Could not open Google Drive picker. You may need to reconnect Google in Settings.'
      )
    } finally {
      setDrivePicking(false)
    }
  }

  const { data: allDocs = [], isLoading } = useQuery({
    queryKey: ['documents', mandateId],
    queryFn: () => api.get(`/api/mandates/${mandateId}/documents`).then(r => r.data.data ?? []),
  })
  // Engagement letters show under Materials instead (EngagementLetters).
  const docs = allDocs.filter(d => d.document_type !== 'Engagement Letter')

  const deleteMutation = useMutation({
    mutationFn: id => api.delete(`/api/documents/${id}`).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['documents', mandateId] }),
  })

  function handleDelete(doc) {
    if (!window.confirm(`Delete "${doc.file_name}"? This cannot be undone.`)) return
    deleteMutation.mutate(doc.document_id)
  }

  return (
    <div className="card" style={{ marginBottom: 20 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: docs.length > 0 ? 14 : 0 }}>
        <div>
          <h2 style={{ fontSize: 14, fontWeight: 600 }}>Documents</h2>
          {!isLoading && (
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>
              {docs.length} file{docs.length !== 1 ? 's' : ''}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-secondary btn-sm" onClick={handleAttachFromDrive} disabled={drivePicking}>
            {drivePicking ? 'Opening Drive…' : '+ Attach from Drive'}
          </button>
          <button className="btn btn-primary btn-sm" onClick={() => setUploadOpen(true)}>
            + Upload Document
          </button>
        </div>
      </div>

      {driveErr && <ErrorBanner msg={driveErr} />}

      {isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '28px 0' }}>
          <div className="spinner" />
        </div>
      ) : docs.length === 0 ? (
        <div className="empty-state" style={{ padding: '28px 20px', marginTop: 14 }}>
          <h3 style={{ fontSize: 13 }}>No documents yet</h3>
          <p>Upload a PPM or other mandate documents. Engagement letters are under Materials.</p>
        </div>
      ) : (
        <div style={{ border: '1px solid var(--border)', borderRadius: 6, overflow: 'hidden' }}>
          {/* Table header */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '2fr 160px 70px 90px 1fr 36px',
            padding: '7px 14px',
            background: 'var(--surface-raised)',
            borderBottom: '1px solid var(--border)',
            fontSize: 11, fontWeight: 600, textTransform: 'uppercase',
            letterSpacing: '0.06em', color: 'var(--text-secondary)',
            gap: 12,
          }}>
            <span>File</span>
            <span>Type</span>
            <span>Size</span>
            <span>Uploaded</span>
            <span>Notes</span>
            <span />
          </div>

          {/* Rows */}
          {docs.map((doc, idx) => (
            <div key={doc.document_id} style={{
              display: 'grid',
              gridTemplateColumns: '2fr 160px 70px 90px 1fr 36px',
              padding: '10px 14px',
              borderBottom: idx < docs.length - 1 ? '1px solid var(--border)' : 'none',
              alignItems: 'center', gap: 12,
              fontSize: 13,
            }}>
              {/* File name — click to download, or opens in Drive for linked docs */}
              <a
                href={doc.signed_url || doc.drive_web_view_link}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  color: 'var(--accent)', fontWeight: 500, fontSize: 13,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  textDecoration: 'none',
                }}
                title={doc.source === 'google_drive' ? `${doc.file_name} (Google Drive)` : doc.file_name}
              >
                {doc.source === 'google_drive' && doc.drive_icon_link && (
                  <img src={doc.drive_icon_link} alt="" width={14} height={14} style={{ flexShrink: 0 }} />
                )}
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {doc.file_name}
                </span>
              </a>

              {/* Document type badge */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                {doc.document_type
                  ? <span className={`badge ${DOC_TYPE_BADGE[doc.document_type] ?? 'badge-gray'}`} style={{ fontSize: 10 }}>
                      {doc.document_type}
                    </span>
                  : <span style={{ color: 'var(--text-muted)' }}>—</span>
                }
                {doc.document_type === 'Engagement Letter' && (
                  <button
                    type="button"
                    onClick={() => setSigningDoc(doc)}
                    className={`badge ${doc.is_executed ? 'badge-green' : 'badge-gray'}`}
                    style={{ fontSize: 10, border: 'none', cursor: 'pointer' }}
                    title={doc.is_executed ? 'Signed. Click to change.' : 'Mark this engagement letter signed'}
                  >
                    {doc.is_executed
                      ? `Signed${doc.executed_date ? ' ' + formatDate(doc.executed_date) : ''}`
                      : 'Mark signed'}
                  </button>
                )}
              </div>

              {/* Size (Drive-linked docs have no size — nothing was uploaded) */}
              <span style={{ color: 'var(--text-secondary)' }}>
                {doc.source === 'google_drive' ? 'Drive' : formatFileSize(doc.file_size)}
              </span>

              {/* Upload date */}
              <span style={{ color: 'var(--text-secondary)' }}>{formatDate(doc.created_at)}</span>

              {/* Notes (truncated) */}
              <span style={{
                color: 'var(--text-secondary)', fontSize: 12,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }} title={doc.notes ?? ''}>
                {doc.notes ? (doc.notes.length > 60 ? doc.notes.slice(0, 60) + '…' : doc.notes) : '—'}
              </span>

              {/* Delete */}
              <button
                className="btn-icon"
                onClick={() => handleDelete(doc)}
                disabled={deleteMutation.isPending}
                title="Delete document"
                style={{ color: 'var(--text-muted)', fontSize: 14 }}
              >
                ✕
              </button>
            </div>
          ))}
        </div>
      )}

      {signingDoc && (
        <MarkSignedModal
          doc={signingDoc}
          onClose={() => setSigningDoc(null)}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ['documents', mandateId] })
            qc.invalidateQueries({ queryKey: ['mandate', mandateId] })
          }}
        />
      )}

      {uploadOpen && (
        <UploadDocumentModal
          mandateId={mandateId}
          onClose={() => setUploadOpen(false)}
          onUploaded={() => qc.invalidateQueries({ queryKey: ['documents', mandateId] })}
        />
      )}

      {driveFile && (
        <DriveLinkModal
          mandateId={mandateId}
          driveFile={driveFile}
          onClose={() => setDriveFile(null)}
          onLinked={() => qc.invalidateQueries({ queryKey: ['documents', mandateId] })}
        />
      )}
    </div>
  )
}

// ── Engagement letters (Materials, RGA only) ─────────────────────────────
// The mandate's engagement letters as cards under Materials (Travis, 10/28).
// RGA only: the portal never sees them (mandate_documents_portal excludes
// Engagement Letter rows, migrations/079). Signed ones first, newest first.
function EngagementLetters({ mandateId }) {
  const qc = useQueryClient()
  const [uploadOpen, setUploadOpen] = useState(false)
  const [signingDoc, setSigningDoc] = useState(null)
  const { data: allDocs = [], isLoading } = useQuery({
    queryKey: ['documents', mandateId],
    queryFn: () => api.get(`/api/mandates/${mandateId}/documents`).then(r => r.data.data ?? []),
  })
  const els = allDocs
    .filter(d => d.document_type === 'Engagement Letter' && d.is_latest !== false)
    .sort((a, b) => (b.is_executed - a.is_executed) || (b.created_at > a.created_at ? 1 : -1))
  const deleteMutation = useMutation({
    mutationFn: id => api.delete(`/api/documents/${id}`).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['documents', mandateId] }),
  })
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['documents', mandateId] })
    qc.invalidateQueries({ queryKey: ['mandate', mandateId] })
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>Engagement Letter</span>
          <span className="badge badge-gray" style={{ fontSize: 10 }}>RGA only</span>
        </div>
        <button className="btn btn-secondary btn-sm" onClick={() => setUploadOpen(true)}>+ Upload Engagement Letter</button>
      </div>
      {isLoading ? <div className="spinner" /> : !els.length ? (
        <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>No engagement letter on file.</div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 16 }}>
          {els.map(doc => (
            <SlotCard key={doc.document_id}
              slot={{ material_type: 'Engagement Letter', document: { ...doc, url: doc.signed_url || doc.drive_web_view_link } }}>
              <div style={{ padding: '0 12px 12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                <button type="button" onClick={() => setSigningDoc(doc)}
                  className={`badge ${doc.is_executed ? 'badge-green' : 'badge-gray'}`}
                  style={{ fontSize: 10, border: 'none', cursor: 'pointer' }}
                  title={doc.is_executed ? 'Signed. Click to change.' : 'Mark this engagement letter signed'}>
                  {doc.is_executed ? `Signed${doc.executed_date ? ' ' + formatDate(doc.executed_date) : ''}` : 'Mark signed'}
                </button>
                <button className="btn-icon" title="Delete engagement letter" disabled={deleteMutation.isPending}
                  onClick={() => { if (window.confirm(`Delete "${doc.file_name}"? This cannot be undone.`)) deleteMutation.mutate(doc.document_id) }}
                  style={{ color: 'var(--text-muted)', fontSize: 14 }}>✕</button>
              </div>
            </SlotCard>
          ))}
        </div>
      )}
      {signingDoc && <MarkSignedModal doc={signingDoc} onClose={() => setSigningDoc(null)} onSaved={refresh} />}
      {uploadOpen && <UploadDocumentModal mandateId={mandateId} initialType="Engagement Letter"
        onClose={() => setUploadOpen(false)} onUploaded={refresh} />}
    </div>
  )
}

// ── Section 1: Mandate Header ─────────────────────────────────────────────

// One fact per box on the mandate header.
function Tile({ label, children, warn = false, span = 1 }) {
  return (
    <div style={{
      gridColumn: span > 1 ? `span ${span}` : undefined,
      border: `1px solid ${warn ? 'rgba(245,158,11,0.55)' : 'var(--border)'}`,
      background: warn ? 'var(--warning-muted)' : 'var(--bg)',
      borderRadius: 8, padding: '10px 12px', minWidth: 0,
    }}>
      <div style={{ fontSize: 10.5, fontWeight: 600, textTransform: 'uppercase',
        letterSpacing: '0.06em', color: 'var(--text-secondary)', marginBottom: 6 }}>
        {label}
      </div>
      <div style={{ fontSize: 13, color: 'var(--text-primary)' }}>{children}</div>
    </div>
  )
}

// A box label with its own Edit link, for boxes edited apart from the
// mandate (Key Metrics, Economics).
const boxAction = { background: 'none', border: 'none', padding: 0, cursor: 'pointer',
  font: 'inherit', fontSize: 10.5, color: 'var(--accent)', letterSpacing: '0.06em', textTransform: 'uppercase' }

// extra: more actions shown to the left of Edit, [{ label, onClick }].
function BoxLabel({ text, onEdit, extra = [] }) {
  return (
    <span style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
      {text}
      <span style={{ display: 'flex', gap: 12 }}>
        {extra.map(a => <button key={a.label} type="button" onClick={a.onClick} style={boxAction}>{a.label}</button>)}
        <button type="button" onClick={onEdit} style={boxAction}>Edit</button>
      </span>
    </span>
  )
}

function TagList({ tags, badge = 'badge-gray' }) {
  if (!tags?.length) return '--'
  return (
    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
      {tags.map(t => <span key={t} className={`badge ${badge}`} style={{ fontSize: 10 }}>{t}</span>)}
    </div>
  )
}

function MandateHeader({ mandate, onEditSaved }) {
  const [editOpen, setEditOpen] = useState(false)
  const qc = useQueryClient()
  // Which facts box is being edited: 'keyMetrics' | 'economics' | null.
  const [factsOpen, setFactsOpen] = useState(null)
  const { data: keyMetrics } = useMandateFacts('keyMetrics', mandate.mandate_id)
  const { data: economics } = useMandateFacts('economics', mandate.mandate_id)
  const { data: searchKeywords } = useSearchKeywords(mandate.mandate_id)
  const [keywordsOpen, setKeywordsOpen] = useState(false)
  const [accessOpen, setAccessOpen] = useState(false)

  // Same cache as DocumentsSection. A LIVE mandate with no signed EL on file
  // (grandfathered from before migrations/066) gets a warning.
  const { data: mandateDocs } = useQuery({
    queryKey: ['documents', mandate.mandate_id],
    queryFn: () => api.get(`/api/mandates/${mandate.mandate_id}/documents`).then(r => r.data.data ?? []),
  })
  const missingSignedEl = mandate.bd_stage === 'LIVE' && mandateDocs &&
    !mandateDocs.some(d => d.document_type === 'Engagement Letter' && d.is_executed)

  const { data: firmsData } = useQuery({
    queryKey: ['firms'],
    queryFn: () => api.get('/api/firms').then(r => r.data.data ?? []),
    enabled: editOpen,
  })
  const partnerFirms = (firmsData ?? []).filter(isPartnerFirm)

  const { data: usersData } = useQuery({
    queryKey: ['users'],
    queryFn: () => api.get('/api/users').then(r => r.data.data ?? []),
  })

  const updateMutation = useMutation({
    mutationFn: payload => api.patch(`/api/mandates/${mandate.mandate_id}`, payload).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['mandate', mandate.mandate_id] })
      setEditOpen(false)
    },
  })

  return (
    <div className="card" style={{ marginBottom: 20 }}>
      {/* Top row */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 4 }}>
            <h1 style={{ fontSize: 22, fontWeight: 700 }}>{mandate.mandate_name}</h1>
            <span className={`badge ${BD_BADGE[mandate.bd_stage] ?? 'badge-gray'}`}>{mandate.bd_stage}</span>
            <span className={`badge ${mandate.status === 'Active' ? 'badge-green' : 'badge-gray'}`}>{mandate.status}</span>
            <span className={`badge ${mandate.coverage_type === 'Partnered' ? 'badge-purple' : 'badge-gray'}`}>
              {mandate.coverage_type ?? 'Solo'}
            </span>
          </div>
          <div style={{ fontSize: 14, color: 'var(--text-secondary)' }}>{mandate.gp_firm?.name ?? '--'}</div>
        </div>
        <button className="btn btn-secondary btn-sm" onClick={() => setEditOpen(true)}>
          Edit
        </button>
      </div>

      {/* Every fact in its own box, so nothing runs together. */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))', gap: 10 }}>
        {/* Same text partners see in the portal. target_raise (numeric, never
            filled on any mandate as of 10/23) stays in the schema, unshown. */}
        <Tile label="Capital Need">{mandate.capital_need || '--'}</Tile>
        <Tile label="Target Close">{mandate.target_close || '--'}</Tile>
        <Tile label="Timing">{mandate.timing || '--'}</Tile>
        <Tile label="Engagement Letter" warn={missingSignedEl}>
          {mandate.engagement_letter_date ? formatDate(mandate.engagement_letter_date) : '--'}
          {missingSignedEl && (
            <div style={{ fontSize: 11, color: 'var(--warning-text, #b45309)', marginTop: 4 }}>No signed EL on file</div>
          )}
        </Tile>
        <Tile label="Relationship Owner">{
          (() => {
            const ids = Array.isArray(mandate.relationship_owner) ? mandate.relationship_owner : (mandate.relationship_owner ? [mandate.relationship_owner] : [])
            if (!ids.length) return '--'
            return ids.map(id => (
              <div key={id}>{(usersData ?? []).find(u => u.id === id)?.name ?? id.slice(0, 8) + '…'}</div>
            ))
          })()
        }</Tile>
        <Tile label="Introduced Via">{mandate.introducer
          ? <PeekLink type="contact" id={mandate.introducer.contact_id}>{mandate.introducer.name}</PeekLink>
          : '--'}</Tile>
        {mandate.coverage_type === 'Partnered' && (
          <Tile label="Partner">{mandate.partner_firm?.name ?? '--'}</Tile>
        )}
        <Tile label="Work Type (per EL)">
          {(mandate.work_type?.length > 0 || mandate.work_subtype?.length > 0)
            ? <WorkTypeTags workType={mandate.work_type} workSubtype={mandate.work_subtype} />
            : '--'}
        </Tile>
        {(mandate.bd_targets || mandate.work_subtype?.includes(BUSINESS_DEVELOPMENT)) && (
          <Tile label="Business Development Targets">{mandate.bd_targets || '--'}</Tile>
        )}
        {/* The older free-tag fields duplicate the Taxonomy box (structure,
            sector, geography), so they show only on a mandate with no
            taxonomy tags yet (pipeline mandates such as Delos). */}
        {!mandate.taxonomy_tags?.length && (mandate.product_type?.length > 0 || mandate.strategy_sector?.length > 0 || mandate.geography_focus?.length > 0) && (
          <>
            {mandate.product_type?.length > 0 && <Tile label="Product Type"><TagList tags={mandate.product_type} /></Tile>}
            {mandate.strategy_sector?.length > 0 && <Tile label="Strategy / Sector"><TagList tags={mandate.strategy_sector} /></Tile>}
            {mandate.geography_focus?.length > 0 && <Tile label="Geography"><TagList tags={mandate.geography_focus} badge="badge-blue" /></Tile>}
          </>
        )}
        {/* Key Metrics (migrations/082), the set following Fund / Direct
            Deal, and Economics (migrations/083), RGA's terms from the EL. */}
        {keyMetrics?.metrics?.length > 0 && (
          <Tile span={2} label={<BoxLabel text={`Key Metrics · ${keyMetrics.mandate_type === 'FS' ? 'Fund' : 'Direct Deal'}`}
            onEdit={() => setFactsOpen('keyMetrics')} />}>
            <FactRows rows={keyMetrics.metrics} />
          </Tile>
        )}
        {economics?.metrics?.length > 0 && (
          <Tile span={2} label={<BoxLabel text="Economics · per EL" onEdit={() => setFactsOpen('economics')}
            extra={[{ label: 'Access', onClick: () => setAccessOpen(true) }]} />}>
            <FactRows rows={economics.metrics} />
          </Tile>
        )}
        {mandate.taxonomy_tags?.length > 0 && (
          <Tile label={`Taxonomy${mandate.mandate_type ? ` · ${mandate.mandate_type === 'FS' ? 'Fund' : 'Direct Deal'}` : ''}`} span={2}>
            <MandateTaxonomyTags tags={mandate.taxonomy_tags} />
          </Tile>
        )}
        {/* Search keywords (migrations/086): the words that bring this
            mandate up in global search. RGA only. */}
        <Tile span={2} label={<SearchKeywordsLabel mandateId={mandate.mandate_id} onEdit={() => setKeywordsOpen(true)} />}>
          <SearchKeywordChips data={searchKeywords} />
        </Tile>
      </div>

      {accessOpen && <EconomicsAccessModal mandateId={mandate.mandate_id} onClose={() => setAccessOpen(false)} />}

      {keywordsOpen && (
        <EditSearchKeywordsModal mandateId={mandate.mandate_id} keywords={searchKeywords?.keywords}
          onClose={() => setKeywordsOpen(false)} />
      )}

      {factsOpen && (
        <EditFactsModal kind={factsOpen} mandateId={mandate.mandate_id}
          title={factsOpen === 'economics' ? 'Edit Economics' : 'Edit Key Metrics'}
          metrics={(factsOpen === 'economics' ? economics : keyMetrics).metrics}
          onClose={() => setFactsOpen(null)} />
      )}

      {/* Edit modal */}
      {editOpen && (
        <EditMandateModal
          mandate={mandate}
          partnerFirms={partnerFirms}
          onClose={() => setEditOpen(false)}
          onSave={updateMutation.mutateAsync}
          saving={updateMutation.isPending}
          saveError={updateMutation.error?.response?.data?.error}
        />
      )}
    </div>
  )
}

// ── Edit Mandate Modal ────────────────────────────────────────────────────

function EditMandateModal({ mandate, partnerFirms, onClose, onSave, saving, saveError }) {
  const [form, setForm] = useState({
    bd_stage:        mandate.bd_stage,
    coverage_type:   mandate.coverage_type ?? 'Solo',
    partner_firm_id: mandate.partner_firm_id ?? '',
    work_type:       mandate.work_type ?? [],
    work_subtype:    mandate.work_subtype ?? [],
    introduced_via:  mandate.introduced_via ?? null,
    capital_need:    mandate.capital_need ?? '',
    target_close:    mandate.target_close ?? '',
    timing:          mandate.timing ?? '',
    bd_targets:      mandate.bd_targets ?? '',
  })
  const [err, setErr] = useState(null)
  const [introducerName, setIntroducerName] = useState(mandate.introducer?.name ?? null)
  const [pickingIntroducer, setPickingIntroducer] = useState(false)

  const { data: allContacts = [] } = useQuery({
    queryKey: ['contacts'],
    queryFn: () => api.get('/api/contacts').then(r => r.data.data ?? []),
    enabled: pickingIntroducer,
  })

  function set(k, v) { setForm(p => ({ ...p, [k]: v })); setErr(null) }

  // Ticking a sub-type ticks its type too; unticking leaves the type alone.
  function toggleWorkSubtype(t, sub) {
    setForm(p => {
      const on = p.work_subtype.includes(sub)
      return {
        ...p,
        work_subtype: on ? p.work_subtype.filter(x => x !== sub) : [...p.work_subtype, sub],
        work_type: !on && !p.work_type.includes(t) ? [...p.work_type, t] : p.work_type,
      }
    })
    setErr(null)
  }

  function toggleWorkType(t) {
    setForm(p => ({
      ...p,
      // Unticking a type also clears its sub-types (the database would add
      // the type back for any sub-type left behind).
      ...(p.work_type.includes(t)
        ? { work_type: p.work_type.filter(x => x !== t),
            work_subtype: p.work_subtype.filter(s => !WORK_TYPE_TREE.find(w => w.type === t).subtypes.includes(s)) }
        : { work_type: [...p.work_type, t] }),
    }))
    setErr(null)
  }

  async function handleSubmit(e) {
    e.preventDefault()
    const payload = { ...form }
    if (payload.coverage_type !== 'Partnered') payload.partner_firm_id = null
    if (!payload.partner_firm_id) delete payload.partner_firm_id
    if (!payload.work_type.length) payload.work_type = null
    if (!payload.work_subtype.length) payload.work_subtype = null
    for (const k of ['capital_need', 'target_close', 'timing', 'bd_targets']) payload[k] = payload[k].trim() || null
    try { await onSave(payload) }
    catch (e) { setErr(e.response?.data?.error || e.message) }
  }

  return (
    <Modal title="Edit Mandate" onClose={onClose}>
      <form onSubmit={handleSubmit} style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>
        <ErrorBanner msg={err || saveError} />

        {/* Status (Active / Inactive) follows from the stage; set in the database. */}
        <div className="input-group">
          <label className="input-label">BD Stage</label>
          <select className="select" value={form.bd_stage} onChange={e => set('bd_stage', e.target.value)}>
            {BD_STAGES.map(s => <option key={s}>{s}</option>)}
          </select>
        </div>

        <div className="input-group">
          <label className="input-label">Coverage Type</label>
          <div style={{ display: 'flex', gap: 20, marginTop: 6 }}>
            {['Solo', 'Partnered'].map(t => (
              <label key={t} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer',
                fontSize: 13, color: form.coverage_type === t ? 'var(--text-primary)' : 'var(--text-secondary)',
                fontWeight: form.coverage_type === t ? 500 : 400 }}>
                <input type="radio" name="cov" value={t} checked={form.coverage_type === t}
                  onChange={() => set('coverage_type', t)} style={{ accentColor: 'var(--accent)', cursor: 'pointer' }} />
                {t}
              </label>
            ))}
          </div>
        </div>

        {form.coverage_type === 'Partnered' && (
          <div className="input-group">
            <label className="input-label">Partner Firm</label>
            <EntityField kind="firm" records={partnerFirms} value={form.partner_firm_id}
              onChange={id => set('partner_firm_id', id ?? '')} allowClear={false}
              createDefaults={{ firm_type: [PARTNER_FIRM] }} />
          </div>
        )}

        <div className="input-group">
          <label className="input-label">Introduced Via</label>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 6 }}>
            The person who brought this deal to RGA
          </div>
          {pickingIntroducer ? (
            <ContactTypeahead
              contacts={allContacts}
              placeholder="Search contacts…"
              clearLabel="No one"
              onSave={id => {
                set('introduced_via', id)
                setIntroducerName(id ? allContacts.find(c => c.contact_id === id)?.name : null)
                setPickingIntroducer(false)
              }}
              onCancel={() => setPickingIntroducer(false)}
            />
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13 }}>
              <span style={{ color: introducerName ? 'var(--text-primary)' : 'var(--text-muted)' }}>
                {introducerName ?? 'Not set'}
              </span>
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => setPickingIntroducer(true)}>
                {introducerName ? 'Change' : 'Set'}
              </button>
            </div>
          )}
        </div>

        {/* Partner-facing: shown as written on the portal mandate page. */}
        <div className="input-group">
          <label className="input-label">Capital Need <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(shown to partners)</span></label>
          <textarea className="textarea" rows={2} value={form.capital_need} onChange={e => set('capital_need', e.target.value)} />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div className="input-group">
            <label className="input-label">Target Close <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(shown to partners)</span></label>
            <input className="input" value={form.target_close} onChange={e => set('target_close', e.target.value)} />
          </div>
          <div className="input-group">
            <label className="input-label">Timing <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(engaged partners)</span></label>
            <input className="input" value={form.timing} onChange={e => set('timing', e.target.value)} />
          </div>
        </div>

        <div className="input-group">
          <label className="input-label">Work Type <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(per the signed EL)</span></label>
          <div style={{ display: 'flex', gap: 28, marginTop: 6, flexWrap: 'wrap' }}>
            {WORK_TYPE_TREE.map(({ type: t, subtypes }) => (
              <div key={t} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer',
                  fontSize: 13, color: form.work_type.includes(t) ? 'var(--text-primary)' : 'var(--text-secondary)',
                  fontWeight: form.work_type.includes(t) ? 500 : 400 }}>
                  <input type="checkbox" checked={form.work_type.includes(t)}
                    onChange={() => toggleWorkType(t)} style={{ accentColor: 'var(--accent)', cursor: 'pointer' }} />
                  {t}
                </label>
                {subtypes.map(sub => (
                  <label key={sub} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer',
                    fontSize: 12, marginLeft: 22,
                    color: form.work_subtype.includes(sub) ? 'var(--text-primary)' : 'var(--text-secondary)' }}>
                    <input type="checkbox" checked={form.work_subtype.includes(sub)}
                      onChange={() => toggleWorkSubtype(t, sub)} style={{ accentColor: 'var(--accent)', cursor: 'pointer' }} />
                    {sub}
                  </label>
                ))}
              </div>
            ))}
          </div>
        </div>

        {(form.bd_targets || form.work_subtype.includes(BUSINESS_DEVELOPMENT)) && (
          <div className="input-group">
            <label className="input-label">Business Development Targets <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(engaged partners)</span></label>
            <textarea className="textarea" rows={2} value={form.bd_targets} onChange={e => set('bd_targets', e.target.value)}
              placeholder="Who the names should be, e.g. large grocery, dairy producers" />
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, paddingTop: 4,
          borderTop: '1px solid var(--border)', marginTop: 4 }}>
          <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </form>
    </Modal>
  )
}

// ── Section 2: Investor Pipeline ──────────────────────────────────────────

// ── Investor card ─────────────────────────────────────────────────────────

function InvestorCard({ entry, onClick, onUpdate, onDelete }) {
  const since = daysSince(entry.last_activity)
  const [advisorPickerOpen, setAdvisorPickerOpen] = useState(false)
  const { data: allContacts = [] } = useQuery({
    queryKey: ['contacts'],
    queryFn: () => api.get('/api/contacts').then(r => r.data.data ?? []),
    enabled: advisorPickerOpen,
  })
  const partnerAdvisors = allContacts.filter(isRgaPartner)

  async function saveAdvisor(contactId) {
    setAdvisorPickerOpen(false)
    await onUpdate(entry.pipeline_id, { partner_advisor_id: contactId || null })
  }

  return (
    <div onClick={onClick} style={{
      background: 'var(--surface)', border: '1px solid var(--border)',
      borderRadius: 6, padding: '12px 13px', cursor: 'pointer',
      transition: 'border-color 0.12s, background 0.12s',
    }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--border-light)'; e.currentTarget.style.background = 'var(--surface-raised)' }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.background = 'var(--surface)' }}
    >
      {/* Contact + firm */}
      <div style={{ marginBottom: 8 }}>
        <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-primary)', marginBottom: 2 }}>
          {entry.investor_contact?.name ?? '—'}
        </div>
        <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
          {entry.investor_firm?.name ?? '—'}
        </div>
      </div>

      {/* Badges */}
      <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginBottom: 8, width: '100%' }}>
        <span className={`badge ${PL_BADGE[entry.stage] ?? 'badge-gray'}`} style={{ whiteSpace: 'normal', textAlign: 'left', maxWidth: '100%' }}>{entry.stage}</span>
        {entry.on_engagement_list && (
          <span className="badge badge-green" style={{ fontSize: 10 }}>On EL</span>
        )}
      </div>

      {/* Footer */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 11 }}>
        <div style={{ color: 'var(--text-secondary)', fontWeight: 600 }}>
          {entry.stage === 'Closed' && entry.actual_commitment
            ? formatRaise(entry.actual_commitment)
            : formatRaise(entry.target_commitment) ?? '—'}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, position: 'relative' }}>
          {advisorPickerOpen ? (
            <ContactTypeahead
              contacts={partnerAdvisors}
              placeholder="Search RGA partners…"
              clearLabel="— Clear RGA partner —"
              createDefaults={{ contact_type: [RGA_PARTNER] }}
              onSave={saveAdvisor}
              onCancel={() => setAdvisorPickerOpen(false)}
            />
          ) : (
            <span style={{ color: 'var(--text-muted)' }}>
              {since ?? (entry.partner_advisor ? `via ${entry.partner_advisor.name.split(' ')[0]}` : null)}
            </span>
          )}
          {!advisorPickerOpen && (
            <button
              onClick={e => { e.stopPropagation(); setAdvisorPickerOpen(true) }}
              style={{ background: 'transparent', border: 'none', cursor: 'pointer',
                color: 'var(--text-muted)', padding: '2px 3px', borderRadius: 3,
                display: 'inline-flex', alignItems: 'center' }}
              onMouseEnter={e => e.currentTarget.style.color = 'var(--accent)'}
              onMouseLeave={e => e.currentTarget.style.color = 'var(--text-muted)'}
              title={entry.partner_advisor ? `Change partner advisor (currently ${entry.partner_advisor.name})` : 'Set partner advisor'}
            ><EditIcon /></button>
          )}
          <button
            onClick={e => { e.stopPropagation(); onDelete(entry) }}
            style={{ background: 'transparent', border: 'none', cursor: 'pointer',
              color: 'var(--text-muted)', padding: '2px 3px', borderRadius: 3,
              display: 'inline-flex', alignItems: 'center' }}
            onMouseEnter={e => e.currentTarget.style.color = 'var(--danger)'}
            onMouseLeave={e => e.currentTarget.style.color = 'var(--text-muted)'}
            title="Delete investor"
          ><TrashIcon /></button>
        </div>
      </div>
    </div>
  )
}

// ── Investor detail modal ─────────────────────────────────────────────────

function InvestorDetailModal({ entry, onClose, onUpdate, onLogInteraction, onDelete }) {
  const navigate = useNavigate()
  const [stageOpen, setStageOpen] = useState(false)
  const [notes, setNotes] = useState(entry.notes ?? '')
  const [notesSaved, setNotesSaved] = useState(false)
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState(null)

  // The firm's contacts, listed first in the Investor Contact picker
  const { data: firmContactsData = [] } = useQuery({
    queryKey: ['contacts', { firm_id: entry.investor_firm_id }],
    queryFn: () => api.get('/api/contacts', { params: { firm_id: entry.investor_firm_id } }).then(r => r.data.data ?? []),
    enabled: !!entry.investor_firm_id,
  })

  // Interactions linked to this investor — prefer firm-scoped (broader) when
  // firm_id is available; fall back to contact-scoped query.
  const { data: interactions = [], isLoading: interactionsLoading } = useQuery({
    queryKey: entry.investor_firm_id
      ? ['firm-interactions', entry.investor_firm_id]
      : ['contact-interactions', entry.investor_contact_id],
    queryFn: entry.investor_firm_id
      ? () => api.get(`/api/firms/${entry.investor_firm_id}/interactions`).then(r => r.data.data ?? [])
      : () => api.get('/api/interactions', { params: { contact_id: entry.investor_contact_id } }).then(r => r.data.data ?? []),
    enabled: !!(entry.investor_firm_id || entry.investor_contact_id),
  })

  // Most recent interaction date — drives the Last Activity field
  const mostRecentDate = interactions.length > 0
    ? interactions.reduce((best, i) => (!best || i.date > best ? i.date : best), null)
    : null

  function intNavTarget() {
    if (entry.investor_firm_id) return `/interactions?firm_id=${entry.investor_firm_id}`
    if (entry.investor_contact_id) return `/interactions?contact_id=${entry.investor_contact_id}`
    return '/interactions'
  }

  function srcLabel(source) {
    if (!source) return 'Manual'
    if (source.includes('Gmail')) return 'Gmail'
    if (source.includes('Calendar')) return 'Cal'
    return 'Manual'
  }

  async function handleUpdate(patch) {
    setSaving(true); setErr(null)
    try { await onUpdate(entry.pipeline_id, patch) }
    catch (e) { setErr(e.response?.data?.error || e.message) }
    finally { setSaving(false) }
  }

  async function saveNotes() {
    await handleUpdate({ notes })
    setNotesSaved(true)
    setTimeout(() => setNotesSaved(false), 2000)
  }

  const recentInteractions = [...interactions]
    .sort((a, b) => (b.date ?? '').localeCompare(a.date ?? ''))
    .slice(0, 5)

  return (
    <Modal title="Investor Detail" onClose={onClose} width={480}>
      <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>
        <ErrorBanner msg={err} />

        {/* Identity */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 24px' }}>
          <Field label="Investor Contact">
            <EntityField kind="contact" value={entry.investor_contact_id}
              disabled={saving} emptyText="— No contact —"
              priorityIds={new Set(firmContactsData.map(c => c.contact_id))}
              createDefaults={{ contact_type: ['Investor'], primary_firm_id: entry.investor_firm_id }}
              onChange={id => handleUpdate({ investor_contact_id: id || null })} />
          </Field>
          <Field label="Investor Firm">{entry.investor_firm?.name ?? '—'}</Field>
          <Field label="RGA Partner">{entry.partner_advisor?.name ?? '—'}</Field>
          <Field label="Last Activity">
            {interactionsLoading
              ? '…'
              : mostRecentDate
                ? formatDate(mostRecentDate)
                : (daysSince(entry.last_activity) ?? '—')}
          </Field>
          <Field label="Target Commitment">{formatRaise(entry.target_commitment) ?? '—'}</Field>
          {entry.actual_commitment && <Field label="Actual Commitment">{formatRaise(entry.actual_commitment)}</Field>}
        </div>

        <div className="divider" />

        {/* Stage update */}
        <div>
          <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase',
            letterSpacing: '0.06em', color: 'var(--text-secondary)', marginBottom: 8 }}>
            Pipeline Stage
          </div>
          <div style={{ position: 'relative', display: 'inline-block' }}>
            <button
              className={`badge ${PL_BADGE[entry.stage] ?? 'badge-gray'}`}
              onClick={() => setStageOpen(v => !v)}
              style={{ cursor: 'pointer', border: 'none', fontFamily: 'inherit', fontSize: 11, whiteSpace: 'normal', textAlign: 'left', maxWidth: 260 }}
              title="Click to change stage"
            >
              {entry.stage} ▾
            </button>
            {stageOpen && (
              <StageMenu
                stages={PIPELINE_STAGES}
                current={entry.stage}
                zIndex={1100}
                onClose={() => setStageOpen(false)}
                onSelect={s => { setStageOpen(false); handleUpdate({ stage: s }) }}
              />
            )}
          </div>
        </div>

        {/* Engagement list toggle */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          background: 'var(--surface-raised)', borderRadius: 6, padding: '10px 14px' }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 500 }}>Engagement List</div>
            <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 }}>
              Formally placed on the engagement list submitted to the GP
            </div>
          </div>
          <button
            onClick={() => handleUpdate({ on_engagement_list: !entry.on_engagement_list })}
            disabled={saving}
            style={{
              width: 40, height: 22, borderRadius: 11, border: 'none', cursor: 'pointer',
              background: entry.on_engagement_list ? 'var(--success)' : 'var(--border-light)',
              transition: 'background 0.2s', flexShrink: 0,
              position: 'relative',
            }}
          >
            <span style={{
              position: 'absolute', top: 3,
              left: entry.on_engagement_list ? 21 : 3,
              width: 16, height: 16, borderRadius: '50%',
              background: '#fff', transition: 'left 0.2s',
            }} />
          </button>
        </div>

        {/* Interactions */}
        {(entry.investor_firm_id || entry.investor_contact_id) && (
          <>
            <div className="divider" />
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase',
                  letterSpacing: '0.06em', color: 'var(--text-secondary)' }}>
                  Interactions {!interactionsLoading && interactions.length > 0 && `(${interactions.length})`}
                </div>
                {interactions.length > 0 && (
                  <button
                    className="btn btn-ghost btn-sm"
                    style={{ fontSize: 11, padding: '2px 8px' }}
                    onClick={() => { onClose(); navigate(intNavTarget()) }}
                  >
                    Show all →
                  </button>
                )}
              </div>

              {interactionsLoading ? (
                <div style={{ fontSize: 12, color: 'var(--text-muted)', padding: '8px 0' }}>Loading…</div>
              ) : recentInteractions.length === 0 ? (
                <div style={{ fontSize: 12, color: 'var(--text-muted)', padding: '8px 0' }}>
                  No interactions logged yet.
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {recentInteractions.map(i => {
                    const d = i.date ? new Date(i.date) : null
                    const dateStr = d
                      ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                      : '—'
                    const title = (i.subject_title || '(no subject)').slice(0, 45)
                    const truncated = (i.subject_title || '').length > 45
                    const src = srcLabel(i.source)
                    return (
                      <div
                        key={i.interaction_id}
                        onClick={() => { onClose(); navigate(intNavTarget()) }}
                        style={{
                          display: 'grid',
                          gridTemplateColumns: '48px 1fr auto',
                          gap: 8,
                          alignItems: 'center',
                          padding: '6px 8px',
                          borderRadius: 6,
                          cursor: 'pointer',
                          background: 'transparent',
                          transition: 'background 0.15s',
                        }}
                        onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-raised)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                      >
                        <span style={{ fontSize: 11, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                          {dateStr}
                        </span>
                        <span style={{ fontSize: 12, color: 'var(--text-primary)', overflow: 'hidden',
                          textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                          title={truncated ? i.subject_title : undefined}>
                          {title}{truncated ? '…' : ''}
                        </span>
                        <span className="badge badge-gray" style={{ fontSize: 10, whiteSpace: 'nowrap' }}>
                          {src}
                        </span>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          </>
        )}

        {/* Notes */}
        <div className="divider" />
        <div className="input-group">
          <label className="input-label">Notes</label>
          <textarea className="textarea" rows={3} value={notes}
            onChange={e => { setNotes(e.target.value); setNotesSaved(false) }}
            placeholder="Add notes about this investor's progress…" />
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 6 }}>
            <button className="btn btn-secondary btn-sm" onClick={saveNotes} disabled={saving}>
              {notesSaved ? '✓ Saved' : saving ? 'Saving…' : 'Save Notes'}
            </button>
          </div>
        </div>

        <div className="divider" />

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <button className="btn btn-secondary btn-sm" onClick={onLogInteraction}>
            + Log Interaction
          </button>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-danger btn-sm" onClick={() => onDelete(entry)}>Delete</button>
            <button className="btn btn-ghost btn-sm" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    </Modal>
  )
}

// ── Add investor modal ────────────────────────────────────────────────────

function AddInvestorModal({ mandateId, onClose, onAdd }) {
  const [form, setForm] = useState({
    investor_firm_id: '', investor_contact_id: '',
    stage: 'Identified', partner_advisor_id: '',
    target_commitment: '', on_engagement_list: false,
  })
  const [submitting, setSub] = useState(false)
  const [err, setErr] = useState(null)

  function set(k, v) { setForm(p => ({ ...p, [k]: v })); setErr(null) }

  const { data: firmsData } = useQuery({
    queryKey: ['firms'],
    queryFn: () => api.get('/api/firms').then(r => r.data.data ?? []),
  })
  const { data: allContacts } = useQuery({
    queryKey: ['contacts'],
    queryFn: () => api.get('/api/contacts').then(r => r.data.data ?? []),
  })
  const { data: firmContacts } = useQuery({
    queryKey: ['contacts', { firm_id: form.investor_firm_id }],
    queryFn: () => api.get('/api/contacts', { params: { firm_id: form.investor_firm_id } }).then(r => r.data.data ?? []),
    enabled: !!form.investor_firm_id,
  })

  const lpFirms    = (firmsData ?? []).filter(f => f.firm_type?.includes('LP / Investor'))
  const advisors   = (allContacts ?? []).filter(isRgaPartner)
  const invContacts = form.investor_firm_id ? (firmContacts ?? []) : []

  async function handleSubmit(e) {
    e.preventDefault()
    if (!form.investor_firm_id) return setErr('Investor firm is required.')
    setSub(true); setErr(null)
    const payload = {
      stage: form.stage,
      investor_firm_id: form.investor_firm_id,
      on_engagement_list: form.on_engagement_list,
    }
    if (form.investor_contact_id) payload.investor_contact_id = form.investor_contact_id
    if (form.partner_advisor_id)  payload.partner_advisor_id  = form.partner_advisor_id
    if (form.target_commitment)   payload.target_commitment   = parseFloat(form.target_commitment)
    try { await onAdd(payload); onClose() }
    catch (e) { setErr(e.response?.data?.error || e.message) }
    finally { setSub(false) }
  }

  return (
    <Modal title="Add Investor to Pipeline" onClose={onClose}>
      <form onSubmit={handleSubmit} style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>
        <ErrorBanner msg={err} />

        <div className="input-group">
          <label className="input-label">Investor Firm *</label>
          {/* LP / Investor firms first; any firm can be picked or created */}
          <EntityField kind="firm" value={form.investor_firm_id} allowClear={false}
            priorityIds={new Set(lpFirms.map(f => f.firm_id))}
            createDefaults={{ firm_type: ['LP / Investor'] }}
            onChange={id => { set('investor_firm_id', id ?? ''); set('investor_contact_id', '') }} />
        </div>

        <div className="input-group">
          <label className="input-label">Investor Contact</label>
          {/* The firm's own contacts first, then everyone */}
          <EntityField kind="contact" value={form.investor_contact_id}
            priorityIds={new Set(invContacts.map(c => c.contact_id))}
            createDefaults={{ contact_type: ['Investor'], primary_firm_id: form.investor_firm_id || null }}
            onChange={id => set('investor_contact_id', id ?? '')} />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div className="input-group">
            <label className="input-label">Stage</label>
            <select className="select" value={form.stage} onChange={e => set('stage', e.target.value)}>
              {PIPELINE_STAGES.map(s => <option key={s}>{s}</option>)}
            </select>
          </div>
          <div className="input-group">
            <label className="input-label">Target Commitment ($)</label>
            <input className="input" type="number" min="0" step="1000000"
              value={form.target_commitment} placeholder="e.g. 25000000"
              onChange={e => set('target_commitment', e.target.value)} />
            {form.target_commitment !== '' && (
              <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 }}>
                = {formatRaise(parseFloat(form.target_commitment))}
              </div>
            )}
          </div>
        </div>

        <div className="input-group">
          <label className="input-label">RGA Partner</label>
          <EntityField kind="contact" records={advisors} value={form.partner_advisor_id}
            createDefaults={{ contact_type: [RGA_PARTNER] }}
            onChange={id => set('partner_advisor_id', id ?? '')} />
        </div>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          background: 'var(--surface-raised)', borderRadius: 6, padding: '10px 14px' }}>
          <label style={{ fontSize: 13, fontWeight: 500, cursor: 'pointer' }} htmlFor="ael">
            Add to Engagement List
          </label>
          <input id="ael" type="checkbox" checked={form.on_engagement_list}
            onChange={e => set('on_engagement_list', e.target.checked)}
            style={{ accentColor: 'var(--accent)', width: 16, height: 16, cursor: 'pointer' }} />
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, paddingTop: 4,
          borderTop: '1px solid var(--border)', marginTop: 4 }}>
          <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={submitting}>
            {submitting ? 'Adding…' : 'Add Investor'}
          </button>
        </div>
      </form>
    </Modal>
  )
}

// ── Export helper ─────────────────────────────────────────────────────────

function todayStr() {
  return new Date().toISOString().slice(0, 10).replace(/-/g, '')
}

function exportPipeline(entries, format) {
  const data = entries.map(e => ({
    Firm: e.investor_firm?.name ?? '',
    Contact: e.investor_contact?.name ?? '',
    'Contact Email': e.investor_contact?.email ?? '',
    Stage: e.stage ?? '',
    'On Engagement List': e.on_engagement_list ? 'Yes' : 'No',
    'RGA Partner': e.partner_advisor?.name ?? '',
    'Target Commitment': e.target_commitment ? formatRaise(e.target_commitment) : '',
    Notes: e.notes ?? '',
  }))
  const ws = XLSX.utils.json_to_sheet(data)
  const wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, ws, 'Pipeline')
  const filename = `RGA_Pipeline_${todayStr()}.${format === 'xlsx' ? 'xlsx' : 'csv'}`
  if (format === 'xlsx') {
    XLSX.writeFile(wb, filename)
  } else {
    XLSX.writeFile(wb, filename, { bookType: 'csv' })
  }
}

// ── Pipeline Table View ───────────────────────────────────────────────────

// Ordered list of columns that support Tab-navigation
const EDITABLE_COLS = ['firm', 'contact', 'owner', 'target', 'notes']

// Contact picker for the pipeline table cells, kanban tiles and forms on this
// page: the shared EntitySearch (type to search, or create the contact on
// the spot) with this page's existing prop names.
//   contacts       — array to search (pre-sorted; priority items first)
//   priorityIds    — optional Set of contact_ids to show above a divider
//   onSave(id)     — commit callback (null = clear)
//   onCancel()     — cancel callback (blur / Escape)
//   createDefaults — defaults for a contact created from here
//   placeholder, clearLabel — display strings
function ContactTypeahead({ contacts, priorityIds = null, onSave, onCancel, placeholder, clearLabel, createDefaults }) {
  return (
    <EntitySearch kind="contact" records={contacts} priorityIds={priorityIds}
      placeholder={placeholder} clearLabel={clearLabel} createDefaults={createDefaults}
      onPick={id => onSave(id)} onCancel={onCancel} />
  )
}

function PipelineTableView({ entries, mandate, onUpdate, onDelete }) {
  const [search, setSearch]           = useState('')
  const [staleOnly, setStaleOnly]     = useState(false)
  const [sortCol, setSortCol]         = useState('firm')
  const [sortDir, setSortDir]         = useState('asc')
  const [openStageId, setOpenStageId] = useState(null)
  // editCell: { rowId, col } | null
  const [editCell, setEditCell]       = useState(null)
  const [editValue, setEditValue]     = useState('')
  const [savingCells, setSavingCells] = useState(new Set())

  // Top scrollbar mirrors the table's horizontal scroll — same reasoning as
  // the kanban board's (see PipelineKanban). The table's width is
  // content-driven rather than a fixed formula, so it's measured via ref
  // instead of computed.
  const tableScrollRef = useRef(null)
  const topTableScrollRef = useRef(null)
  const syncingTableScrollRef = useRef(false)
  const [tableScrollWidth, setTableScrollWidth] = useState(0)

  useEffect(() => {
    const el = tableScrollRef.current
    if (!el) return
    const measure = () => setTableScrollWidth(el.scrollWidth)
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [entries])

  function handleTableScroll(e) {
    if (syncingTableScrollRef.current) { syncingTableScrollRef.current = false; return }
    if (topTableScrollRef.current) {
      syncingTableScrollRef.current = true
      topTableScrollRef.current.scrollLeft = e.target.scrollLeft
    }
  }
  function handleTopTableScroll(e) {
    if (syncingTableScrollRef.current) { syncingTableScrollRef.current = false; return }
    if (tableScrollRef.current) {
      syncingTableScrollRef.current = true
      tableScrollRef.current.scrollLeft = e.target.scrollLeft
    }
  }

  // ── Data for dropdowns ───────────────────────────────────────────────
  const { data: allFirms = [] } = useQuery({
    queryKey: ['firms'],
    queryFn: () => api.get('/api/firms').then(r => r.data.data ?? []),
  })
  const lpFirms = allFirms.filter(f => f.firm_type?.includes('LP / Investor'))

  const { data: allContacts = [] } = useQuery({
    queryKey: ['contacts'],
    queryFn: () => api.get('/api/contacts').then(r => r.data.data ?? []),
  })
  const partnerAdvisors = allContacts.filter(isRgaPartner)

  // Only fetch contacts when a contact cell is actively being edited
  const editingEntry     = editCell ? entries.find(e => e.pipeline_id === editCell.rowId) : null
  const contactFirmId    = editCell?.col === 'contact' ? (editingEntry?.investor_firm_id ?? null) : null
  const { data: firmContacts = [] } = useQuery({
    queryKey: ['contacts', { firm_id: contactFirmId }],
    queryFn: () => api.get('/api/contacts', { params: { firm_id: contactFirmId } }).then(r => r.data.data ?? []),
    enabled: !!contactFirmId,
  })

  // ── Edit helpers ─────────────────────────────────────────────────────

  // Open a cell for editing. Saves the previous cell first (fire-and-forget).
  function startEdit(rowId, col, currentValue) {
    if (editCell?.rowId === rowId && editCell?.col === col) return
    if (editCell) saveEdit(editCell.rowId, editCell.col, editValue)
    setEditCell({ rowId, col })
    setEditValue(String(currentValue ?? ''))
  }

  // Persist a cell value via PATCH, with optimistic save indicator.
  async function saveEdit(rowId, col, value) {
    const entry = entries.find(e => e.pipeline_id === rowId)
    if (!entry) return

    let patch
    if (col === 'firm') {
      if (value === (entry.investor_firm_id ?? '')) return
      // Changing firm resets contact
      patch = { investor_firm_id: value || null, investor_contact_id: null }
    } else if (col === 'contact') {
      if (value === (entry.investor_contact_id ?? '')) return
      patch = { investor_contact_id: value || null }
    } else if (col === 'target') {
      const num = value === '' ? null : parseFloat(value)
      if (num === (entry.target_commitment ?? null)) return
      patch = { target_commitment: num }
    } else if (col === 'owner') {
      if (value === (entry.partner_advisor_id ?? '')) return
      patch = { partner_advisor_id: value || null }
    } else if (col === 'notes') {
      const trimmed = value.trim()
      if (trimmed === (entry.notes ?? '')) return
      patch = { notes: trimmed }
    }
    if (!patch) return

    const key = `${rowId}-${col}`
    setSavingCells(s => new Set([...s, key]))
    try {
      await onUpdate(rowId, patch)
    } finally {
      setSavingCells(s => { const n = new Set(s); n.delete(key); return n })
    }
  }

  function commitEdit() {
    if (!editCell) return
    const { rowId, col } = editCell
    setEditCell(null)
    saveEdit(rowId, col, editValue)
  }

  function cancelEdit() {
    setEditCell(null)
    setEditValue('')
  }

  // Keyboard handler for text/number inputs
  function handleInputKeyDown(e, rowId, col) {
    if (e.key === 'Escape') { e.preventDefault(); cancelEdit(); return }
    if (e.key === 'Enter')  { e.preventDefault(); commitEdit(); return }
    if (e.key === 'Tab') {
      e.preventDefault()
      const entry = entries.find(en => en.pipeline_id === rowId)
      const colIdx = EDITABLE_COLS.indexOf(col)
      // Save current cell
      const val = editValue
      setEditCell(null)
      saveEdit(rowId, col, val)
      // Open next editable cell in same row
      if (colIdx < EDITABLE_COLS.length - 1) {
        const nextCol = EDITABLE_COLS[colIdx + 1]
        const nextVal =
          nextCol === 'firm'    ? (entry?.investor_firm_id ?? '') :
          nextCol === 'contact' ? (entry?.investor_contact_id ?? '') :
          nextCol === 'owner'   ? (entry?.partner_advisor_id ?? '') :
          nextCol === 'target'  ? (entry?.target_commitment != null ? String(entry.target_commitment) : '') :
                                  (entry?.notes ?? '')
        setEditCell({ rowId, col: nextCol })
        setEditValue(String(nextVal))
      }
    }
  }

  // ── Sorting / filtering ──────────────────────────────────────────────

  function toggleSort(col) {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    else { setSortCol(col); setSortDir('asc') }
  }

  const STALE_DAYS = 30
  const isStale = e => !e.last_activity ||
    (Date.now() - new Date(e.last_activity)) / 86_400_000 >= STALE_DAYS

  const filtered = entries.filter(e => {
    if (staleOnly && !isStale(e)) return false
    const q = search.toLowerCase()
    if (!q) return true
    return (
      (e.investor_firm?.name    ?? '').toLowerCase().includes(q) ||
      (e.investor_contact?.name ?? '').toLowerCase().includes(q)
    )
  })

  const sorted = [...filtered].sort((a, b) => {
    let av, bv
    if (sortCol === 'firm')    { av = a.investor_firm?.name    ?? ''; bv = b.investor_firm?.name    ?? '' }
    if (sortCol === 'contact') { av = a.investor_contact?.name ?? ''; bv = b.investor_contact?.name ?? '' }
    if (sortCol === 'stage')   { av = a.stage ?? '';                  bv = b.stage ?? '' }
    if (sortCol === 'owner')   { av = a.partner_advisor?.name ?? ''; bv = b.partner_advisor?.name ?? '' }
    if (sortCol === 'target')  { av = a.target_commitment ?? 0;       bv = b.target_commitment ?? 0 }
    if (sortCol === 'last_activity') { av = a.last_activity ?? '';    bv = b.last_activity ?? '' }
    if (typeof av === 'string') return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av)
    return sortDir === 'asc' ? av - bv : bv - av
  })

  const totalCommitment = filtered.reduce((sum, e) => sum + (e.target_commitment ?? 0), 0)

  // ── Shared styles ────────────────────────────────────────────────────

  const thStyle = (col) => ({
    padding: '8px 12px', fontSize: 11, fontWeight: 600,
    textTransform: 'uppercase', letterSpacing: '0.06em',
    color: 'var(--text-secondary)', cursor: col ? 'pointer' : 'default',
    whiteSpace: 'nowrap', userSelect: 'none', textAlign: 'left',
  })

  const SortArrow = ({ col }) => {
    if (sortCol !== col) return <span style={{ color: 'var(--text-muted)', marginLeft: 3 }}>↕</span>
    return <span style={{ color: 'var(--accent)', marginLeft: 3 }}>{sortDir === 'asc' ? '↑' : '↓'}</span>
  }

  // td style: highlight when this cell is being edited
  function tdStyle(rowId, col, base = {}) {
    const active = editCell?.rowId === rowId && editCell?.col === col
    return {
      padding: active ? '6px 8px' : '10px 12px',
      cursor: 'pointer',
      outline: active ? '2px solid var(--accent)' : 'none',
      outlineOffset: active ? '-2px' : undefined,
      borderRadius: active ? 4 : undefined,
      background: active ? 'rgba(59,130,246,0.07)' : 'transparent',
      transition: 'outline 0.1s, background 0.1s',
      ...base,
    }
  }

  // Tiny spinner shown in cell while saving
  function CellSpinner() {
    return <span style={{
      display: 'inline-block', width: 10, height: 10, marginLeft: 6,
      border: '1.5px solid var(--border-light)', borderTopColor: 'var(--accent)',
      borderRadius: '50%', animation: 'spin 0.6s linear infinite', verticalAlign: 'middle',
    }} />
  }

  // ── Cell renderers ───────────────────────────────────────────────────

  function FirmCell({ e }) {
    const isEditing = editCell?.rowId === e.pipeline_id && editCell?.col === 'firm'
    const isSaving  = savingCells.has(`${e.pipeline_id}-firm`)
    return (
      <td
        style={tdStyle(e.pipeline_id, 'firm', { fontWeight: 500, whiteSpace: 'nowrap' })}
        onClick={() => !isEditing && startEdit(e.pipeline_id, 'firm', e.investor_firm_id ?? '')}
        title={isEditing ? undefined : 'Click to edit firm'}
      >
        {isEditing ? (
          <EntitySearch kind="firm" clearLabel="— No firm —"
            priorityIds={new Set(lpFirms.map(f => f.firm_id))}
            createDefaults={{ firm_type: ['LP / Investor'] }}
            onPick={id => { setEditCell(null); saveEdit(e.pipeline_id, 'firm', id ?? '') }}
            onCancel={cancelEdit} />
        ) : (
          <>
            {e.investor_firm?.name ?? <span style={{ color: 'var(--text-muted)' }}>—</span>}
            {isSaving && <CellSpinner />}
          </>
        )}
      </td>
    )
  }

  function ContactCell({ e }) {
    const isEditing = editCell?.rowId === e.pipeline_id && editCell?.col === 'contact'
    const isSaving  = savingCells.has(`${e.pipeline_id}-contact`)
    return (
      <td
        style={tdStyle(e.pipeline_id, 'contact', { whiteSpace: 'nowrap', color: 'var(--text-secondary)' })}
        onClick={() => !isEditing && startEdit(e.pipeline_id, 'contact', e.investor_contact_id ?? '')}
        title={isEditing ? undefined : 'Click to edit contact'}
      >
        {isEditing ? (() => {
          // Firm's own contacts first (priority), then everyone else.
          const firmIds = new Set(firmContacts.map(c => c.contact_id))
          const ordered = [
            ...firmContacts,
            ...allContacts.filter(c => !firmIds.has(c.contact_id)),
          ]
          return (
            <ContactTypeahead
              contacts={ordered}
              priorityIds={firmIds}
              placeholder="Search contacts…"
              clearLabel="— Clear contact —"
              createDefaults={{ contact_type: ['Investor'], primary_firm_id: e.investor_firm_id }}
              onSave={id => { setEditCell(null); saveEdit(e.pipeline_id, 'contact', id ?? '') }}
              onCancel={cancelEdit}
            />
          )
        })() : (
          <>
            {e.investor_contact?.name
              ? <span>{e.investor_contact.name}</span>
              : <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>+ Add contact</span>}
            {isSaving && <CellSpinner />}
          </>
        )}
      </td>
    )
  }

  function OwnerCell({ e }) {
    const isEditing = editCell?.rowId === e.pipeline_id && editCell?.col === 'owner'
    const isSaving  = savingCells.has(`${e.pipeline_id}-owner`)
    return (
      <td
        style={tdStyle(e.pipeline_id, 'owner', { whiteSpace: 'nowrap' })}
        onClick={() => !isEditing && startEdit(e.pipeline_id, 'owner', e.partner_advisor_id ?? '')}
        title={isEditing ? undefined : 'Click to edit RGA partner'}
      >
        {isEditing ? (
          <ContactTypeahead
            contacts={partnerAdvisors}
            placeholder="Search RGA partners…"
            clearLabel="— Clear RGA partner —"
            createDefaults={{ contact_type: [RGA_PARTNER] }}
            onSave={id => { setEditCell(null); saveEdit(e.pipeline_id, 'owner', id ?? '') }}
            onCancel={cancelEdit}
          />
        ) : (
          <>
            {e.partner_advisor?.name
              ? <span style={{ color: 'var(--text-secondary)' }}>{e.partner_advisor.name}</span>
              : <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>+ Add RGA partner</span>}
            {isSaving && <CellSpinner />}
          </>
        )}
      </td>
    )
  }

  function TargetCell({ e }) {
    const isEditing = editCell?.rowId === e.pipeline_id && editCell?.col === 'target'
    const isSaving  = savingCells.has(`${e.pipeline_id}-target`)
    return (
      <td
        style={tdStyle(e.pipeline_id, 'target', { whiteSpace: 'nowrap', fontWeight: 500 })}
        onClick={() => !isEditing && startEdit(e.pipeline_id, 'target',
          e.target_commitment != null ? String(e.target_commitment) : '')}
        title={isEditing ? undefined : 'Click to edit target commitment'}
      >
        {isEditing ? (
          <input
            autoFocus
            type="number"
            min="0"
            step="1000000"
            value={editValue}
            style={{ ...editInputStyle, width: 120 }}
            onChange={ev => setEditValue(ev.target.value)}
            onBlur={commitEdit}
            onKeyDown={ev => handleInputKeyDown(ev, e.pipeline_id, 'target')}
            placeholder="e.g. 25000000"
          />
        ) : (
          <>
            <span style={{ color: 'var(--text-secondary)' }}>
              {e.target_commitment ? formatRaise(e.target_commitment) : <span style={{ color: 'var(--text-muted)' }}>—</span>}
            </span>
            {isSaving && <CellSpinner />}
          </>
        )}
      </td>
    )
  }

  function NotesCell({ e }) {
    const isEditing = editCell?.rowId === e.pipeline_id && editCell?.col === 'notes'
    const isSaving  = savingCells.has(`${e.pipeline_id}-notes`)
    return (
      <td
        style={tdStyle(e.pipeline_id, 'notes', { maxWidth: 220 })}
        onClick={() => !isEditing && startEdit(e.pipeline_id, 'notes', e.notes ?? '')}
        title={isEditing ? undefined : (e.notes ?? 'Click to add notes')}
      >
        {isEditing ? (
          <input
            autoFocus
            type="text"
            value={editValue}
            style={{ ...editInputStyle, width: '100%', minWidth: 160 }}
            onChange={ev => setEditValue(ev.target.value)}
            onBlur={commitEdit}
            onKeyDown={ev => handleInputKeyDown(ev, e.pipeline_id, 'notes')}
            placeholder="Add notes…"
          />
        ) : (
          <>
            {e.notes
              ? <span style={{ color: 'var(--text-secondary)', fontSize: 12,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block', maxWidth: 200 }}>
                  {e.notes.length > 60 ? e.notes.slice(0, 60) + '…' : e.notes}
                </span>
              : <span style={{ color: 'var(--text-muted)' }}>—</span>}
            {e.partner_notes && (
              <span title={e.partner_notes} style={{ color: 'var(--text-muted)', fontSize: 11, display: 'block', maxWidth: 200,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: 2 }}>
                Partner: {e.partner_notes}
              </span>
            )}
            {isSaving && <CellSpinner />}
          </>
        )}
      </td>
    )
  }

  // ── Render ───────────────────────────────────────────────────────────

  return (
    <div>
      {/* Toolbar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        <input
          className="input"
          placeholder="Search firm or contact…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ maxWidth: 240, fontSize: 13 }}
        />
        <button
          className={`btn btn-sm ${staleOnly ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setStaleOnly(v => !v)}
          title="Show only investors with no activity in the last 30 days"
        >
          Stale (30+ days)
        </button>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <button className="btn btn-secondary btn-sm" onClick={() => exportPipeline(sorted, 'csv')}>Export CSV</button>
          <button className="btn btn-secondary btn-sm" onClick={() => exportPipeline(sorted, 'xlsx')}>Export Excel</button>
        </div>
      </div>

      {/* Top scrollbar — mirrors the table's scroll position below */}
      {tableScrollWidth > 0 && (
        <div
          ref={topTableScrollRef}
          onScroll={handleTopTableScroll}
          style={{
            overflowX: 'auto', overflowY: 'hidden', marginBottom: 6,
            scrollbarWidth: 'thin', scrollbarColor: 'var(--border-light) transparent',
          }}
        >
          <div style={{ width: tableScrollWidth, height: 1 }} />
        </div>
      )}

      {/* Table */}
      <div
        ref={tableScrollRef}
        onScroll={handleTableScroll}
        style={{ border: '1px solid var(--border)', borderRadius: 6, overflow: 'auto' }}
      >
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: 'var(--surface-raised)', borderBottom: '1px solid var(--border)' }}>
              <th style={thStyle('stage')}   onClick={() => toggleSort('stage')}>Stage <SortArrow col="stage" /></th>
              <th style={thStyle('firm')}    onClick={() => toggleSort('firm')}>Firm <SortArrow col="firm" /></th>
              <th style={thStyle('contact')} onClick={() => toggleSort('contact')}>Contact <SortArrow col="contact" /></th>
              <th style={thStyle(null)}>Email</th>
              <th style={thStyle(null)}>On EL</th>
              <th style={thStyle('owner')}   onClick={() => toggleSort('owner')}>RGA Partner <SortArrow col="owner" /></th>
              <th style={thStyle('target')}  onClick={() => toggleSort('target')}>Target <SortArrow col="target" /></th>
              <th style={thStyle(null)}>Notes</th>
              <th style={thStyle('last_activity')} onClick={() => toggleSort('last_activity')}>Last Email <SortArrow col="last_activity" /></th>
              <th style={thStyle(null)}></th>
            </tr>
          </thead>
          <tbody>
            {sorted.length === 0 && (
              <tr>
                <td colSpan={9} style={{ padding: '28px 16px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
                  {search || staleOnly ? 'No results match your filters.' : 'No investors in pipeline.'}
                </td>
              </tr>
            )}
            {sorted.map((e, idx) => (
              <tr key={e.pipeline_id} style={{
                borderBottom: idx < sorted.length - 1 ? '1px solid var(--border)' : 'none',
                background: idx % 2 === 0 ? 'transparent' : 'var(--row-stripe)',
              }}>

                {/* Stage — existing badge dropdown */}
                <td style={{ padding: '10px 12px' }}>
                  <div style={{ position: 'relative', display: 'inline-block' }}>
                    <button
                      className={`badge ${PL_BADGE[e.stage] ?? 'badge-gray'}`}
                      onClick={() => setOpenStageId(v => v === e.pipeline_id ? null : e.pipeline_id)}
                      style={{ cursor: 'pointer', border: 'none', fontFamily: 'inherit', fontSize: 10, whiteSpace: 'normal', textAlign: 'left', maxWidth: 200 }}
                      title="Click to change stage"
                    >
                      {e.stage} ▾
                    </button>
                    {e.stage_date && (
                      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 3 }}
                        title={(e.stage_history ?? []).map(h => `${h.stage}: ${h.occurred_on ?? 'date not set'}`).join('\n')}>
                        since {new Date(e.stage_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </div>
                    )}
                    {openStageId === e.pipeline_id && (
                      <StageMenu
                        stages={PIPELINE_STAGES}
                        current={e.stage}
                        zIndex={300}
                        onClose={() => setOpenStageId(null)}
                        onSelect={s => { setOpenStageId(null); onUpdate(e.pipeline_id, { stage: s }) }}
                      />
                    )}
                  </div>
                </td>

                {/* Firm — editable dropdown */}
                <FirmCell e={e} />

                {/* Contact — editable dropdown (filtered by firm) */}
                <ContactCell e={e} />

                {/* Email — read-only */}
                <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}>
                  {e.investor_contact?.email
                    ? <a href={`mailto:${e.investor_contact.email}`}
                        style={{ color: 'var(--accent)', textDecoration: 'none', fontSize: 12 }}>
                        {e.investor_contact.email}
                      </a>
                    : <span style={{ color: 'var(--text-muted)' }}>—</span>}
                </td>

                {/* On Engagement List — toggle */}
                <td style={{ padding: '10px 12px' }}>
                  <button
                    onClick={() => onUpdate(e.pipeline_id, { on_engagement_list: !e.on_engagement_list })}
                    style={{
                      width: 36, height: 20, borderRadius: 10, border: 'none', cursor: 'pointer',
                      background: e.on_engagement_list ? 'var(--success)' : 'var(--border-light)',
                      transition: 'background 0.2s', position: 'relative', flexShrink: 0,
                    }}
                    title={e.on_engagement_list ? 'On engagement list — click to remove' : 'Not on engagement list — click to add'}
                  >
                    <span style={{
                      position: 'absolute', top: 2,
                      left: e.on_engagement_list ? 18 : 2,
                      width: 16, height: 16, borderRadius: '50%',
                      background: '#fff', transition: 'left 0.2s',
                    }} />
                  </button>
                </td>

                {/* Owner — editable user dropdown */}
                <OwnerCell e={e} />

                {/* Target Commitment — editable number */}
                <TargetCell e={e} />

                {/* Notes — editable text; the partner's own notes (portal) under it */}
                <NotesCell e={e} />

                {/* Last Email — read-only, derived from linked interactions */}
                <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}>
                  {e.last_activity
                    ? <span style={{ color: isStale(e) ? 'var(--danger)' : 'var(--text-secondary)', fontSize: 12 }}>
                        {daysSince(e.last_activity)}
                      </span>
                    : <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>—</span>}
                </td>

                {/* Delete */}
                <td style={{ padding: '10px 8px' }}>
                  <button
                    onClick={() => onDelete(e)}
                    style={{ background: 'transparent', border: 'none', cursor: 'pointer',
                      color: 'var(--text-muted)', padding: '3px 5px', borderRadius: 3,
                      display: 'inline-flex', alignItems: 'center' }}
                    onMouseEnter={ev => ev.currentTarget.style.color = 'var(--danger)'}
                    onMouseLeave={ev => ev.currentTarget.style.color = 'var(--text-muted)'}
                    title="Delete investor"
                  >
                    <TrashIcon />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Footer summary */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginTop: 10, fontSize: 12, color: 'var(--text-secondary)' }}>
        <span>
          {filtered.length} investor{filtered.length !== 1 ? 's' : ''}
          <span style={{ marginLeft: 8, color: 'var(--text-muted)', fontSize: 11 }}>
            · Click any cell in Firm, Contact, RGA Partner, Target, or Notes to edit inline
          </span>
        </span>
        {totalCommitment > 0 && (
          <span>Total target: <strong style={{ color: 'var(--text-primary)' }}>{formatRaise(totalCommitment)}</strong></span>
        )}
      </div>
    </div>
  )
}

// ── Pipeline Kanban ───────────────────────────────────────────────────────

function PipelineKanban({ entries, mandateId, mandate, onEntryUpdate, onEntryAdded }) {
  const [addOpen, setAddOpen]       = useState(false)
  const [detailEntry, setDetail]    = useState(null)
  const [logFromEntry, setLogEntry] = useState(null) // open log modal from inside detail modal
  const [toDelete, setToDelete]     = useState(null)
  const [deleteError, setDeleteError] = useState(null)
  const [pipelineView, setPipelineView] = useState('board')
  const qc = useQueryClient()

  // Top scrollbar mirrors the board's horizontal scroll — with many entries
  // the columns get tall enough that the browser's native scrollbar (which
  // sits at the bottom of the scrollable content) can be a long way down.
  const boardScrollRef = useRef(null)
  const topScrollRef = useRef(null)
  const syncingScrollRef = useRef(false)
  const boardWidth = PIPELINE_STAGES.length * 200 + (PIPELINE_STAGES.length - 1) * 10

  function handleBoardScroll(e) {
    if (syncingScrollRef.current) { syncingScrollRef.current = false; return }
    if (topScrollRef.current) {
      syncingScrollRef.current = true
      topScrollRef.current.scrollLeft = e.target.scrollLeft
    }
  }
  function handleTopScroll(e) {
    if (syncingScrollRef.current) { syncingScrollRef.current = false; return }
    if (boardScrollRef.current) {
      syncingScrollRef.current = true
      boardScrollRef.current.scrollLeft = e.target.scrollLeft
    }
  }

  const addMutation = useMutation({
    mutationFn: payload => api.post(`/api/mandates/${mandateId}/pipeline`, payload).then(r => r.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['pipeline', mandateId] }); onEntryAdded?.() },
  })
  const updateMutation = useMutation({
    mutationFn: ({ id, patch }) => api.patch(`/api/pipeline/${id}`, patch).then(r => r.data),
    onMutate: async ({ id, patch }) => {
      await qc.cancelQueries({ queryKey: ['pipeline', mandateId] })
      const previous = qc.getQueryData(['pipeline', mandateId])
      qc.setQueryData(['pipeline', mandateId], old =>
        (old ?? []).map(e => e.pipeline_id === id ? { ...e, ...patch } : e)
      )
      return { previous }
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(['pipeline', mandateId], ctx.previous)
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ['pipeline', mandateId] }),
    onSuccess: d => {
      setDetail(prev => prev ? { ...prev, ...d.data } : null)
    },
  })

  function handleDragEnd(result) {
    const { destination, source, draggableId } = result
    if (!destination) return
    if (destination.droppableId === source.droppableId && destination.index === source.index) return
    updateMutation.mutate({ id: draggableId, patch: { stage: destination.droppableId } })
  }
  const deleteMutation = useMutation({
    mutationFn: id => api.delete(`/api/pipeline/${id}`).then(r => r.data),
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: ['pipeline', mandateId] })
      setToDelete(null)
      setDeleteError(null)
      setDetail(prev => prev?.pipeline_id === id ? null : prev)
    },
    onError: (err) => {
      setDeleteError(err.response?.data?.error || err.message)
    },
  })

  function cancelDelete() {
    setToDelete(null)
    setDeleteError(null)
  }

  const grouped = Object.fromEntries(
    PIPELINE_STAGES.map(s => [s, entries.filter(e => e.stage === s)])
  )
  const total = entries.length

  return (
    <div>
      {/* Sub-header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 24, marginBottom: 14 }}>
        <div>
          <h2 style={{ fontSize: 14, fontWeight: 600 }}>Investor Pipeline</h2>
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>
            {total} investor{total !== 1 ? 's' : ''} tracked
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <button
            className={`btn btn-sm ${pipelineView === 'board' ? 'btn-primary' : 'btn-ghost'}`}
            onClick={() => setPipelineView('board')}
          >Board</button>
          <button
            className={`btn btn-sm ${pipelineView === 'table' ? 'btn-primary' : 'btn-ghost'}`}
            onClick={() => setPipelineView('table')}
          >Table</button>
          <button className="btn btn-primary btn-sm" onClick={() => setAddOpen(true)}>
            + Add Investor
          </button>
        </div>
      </div>

      {/* Table view */}
      {pipelineView === 'table' && (
        <PipelineTableView
          entries={entries}
          mandate={mandate}
          onUpdate={(id, patch) => updateMutation.mutateAsync({ id, patch })}
          onDelete={e => setToDelete(e)}
        />
      )}

      {/* Kanban scroll container */}
      {pipelineView === 'board' && (
      <>
        {/* Top scrollbar — mirrors the board's scroll position below */}
        <div
          ref={topScrollRef}
          onScroll={handleTopScroll}
          style={{
            overflowX: 'auto', overflowY: 'hidden', marginBottom: 6,
            scrollbarWidth: 'thin', scrollbarColor: 'var(--border-light) transparent',
          }}
        >
          <div style={{ width: boardWidth, height: 1 }} />
        </div>
      <DragDropContext onDragEnd={handleDragEnd}>
        <div
          ref={boardScrollRef}
          onScroll={handleBoardScroll}
          style={{
          display: 'flex', gap: 10, overflowX: 'auto', paddingBottom: 12,
          alignItems: 'flex-start',
          scrollbarWidth: 'thin', scrollbarColor: 'var(--border-light) transparent',
        }}>

          {PIPELINE_STAGES.map(stage => (
            <div key={stage} style={{ minWidth: 200, width: 200, flexShrink: 0 }}>
              {/* Column header */}
              <div style={{
                background: 'var(--surface)', border: '1px solid var(--border)',
                borderBottom: 'none', borderRadius: '6px 6px 0 0',
                borderTop: `3px solid ${PL_ACCENT[stage]}`,
                padding: '8px 10px',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              }}>
                <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.06em',
                  textTransform: 'uppercase', color: 'var(--text-secondary)' }}>
                  {stage}
                </span>
                <span className="badge badge-gray" style={{ fontSize: 10, minWidth: 18, justifyContent: 'center' }}>
                  {grouped[stage].length}
                </span>
              </div>
              {/* Cards with drop zone */}
              <Droppable droppableId={stage}>
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    style={{
                      background: snapshot.isDraggingOver ? 'var(--drop-target)' : 'var(--sidebar-bg)',
                      border: '1px solid var(--border)',
                      borderTop: 'none', borderRadius: '0 0 6px 6px',
                      padding: 7, display: 'flex', flexDirection: 'column', gap: 6,
                      minHeight: 100,
                      transition: 'background 0.15s',
                    }}
                  >
                    {grouped[stage].length === 0 && !snapshot.isDraggingOver && (
                      <div style={{ padding: '14px 8px', textAlign: 'center',
                        fontSize: 11, color: 'var(--text-muted)', fontStyle: 'italic' }}>
                        None
                      </div>
                    )}
                    {grouped[stage].map((e, index) => (
                      <Draggable key={e.pipeline_id} draggableId={e.pipeline_id} index={index}>
                        {(drag, dragSnapshot) => (
                          <div
                            ref={drag.innerRef}
                            {...drag.draggableProps}
                            {...drag.dragHandleProps}
                            style={{
                              borderRadius: 6,
                              opacity: dragSnapshot.isDragging ? 0.92 : 1,
                              boxShadow: dragSnapshot.isDragging ? '0 8px 24px rgba(0,0,0,0.5)' : 'none',
                              ...drag.draggableProps.style,
                            }}
                          >
                            <InvestorCard
                              entry={e}
                              onClick={() => setDetail(e)}
                              onUpdate={(id, patch) => updateMutation.mutateAsync({ id, patch })}
                              onDelete={e => setToDelete(e)}
                            />
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </div>
          ))}
        </div>
      </DragDropContext>
      </>
      )}

      {/* Add investor modal */}
      {addOpen && (
        <AddInvestorModal
          mandateId={mandateId}
          onClose={() => setAddOpen(false)}
          onAdd={addMutation.mutateAsync}
        />
      )}

      {/* Investor detail modal */}
      {detailEntry && !logFromEntry && (
        <InvestorDetailModal
          entry={detailEntry}
          onClose={() => setDetail(null)}
          onUpdate={(id, patch) => updateMutation.mutateAsync({ id, patch })}
          onLogInteraction={() => { setLogEntry(detailEntry); setDetail(null) }}
          onDelete={e => { setDetail(null); setToDelete(e) }}
        />
      )}

      {/* Log interaction modal — triggered from investor detail */}
      {logFromEntry && (
        <LogInteractionModal
          mandateId={mandateId}
          onClose={() => setLogEntry(null)}
          zIndex={1200}
        />
      )}

      {/* Delete confirm */}
      {toDelete && (
        <DeleteConfirmModal
          name={toDelete.investor_contact?.name ?? toDelete.investor_firm?.name ?? 'this investor'}
          onCancel={cancelDelete}
          onConfirm={() => deleteMutation.mutate(toDelete.pipeline_id)}
          deleting={deleteMutation.isPending}
          error={deleteError}
        />
      )}
    </div>
  )
}

// ── Section 3: Log Interaction Modal ─────────────────────────────────────

function LogInteractionModal({ mandateId, onClose, onCreated, zIndex = 1000 }) {
  const [form, setForm] = useState({
    type: 'Meeting', direction: 'Outbound', source: 'Manual',
    subject_title: '', body_notes: '',
    date: new Date().toISOString().slice(0, 16),
    follow_up_due: '', contact_ids: [],
  })
  const [submitting, setSub] = useState(false)
  const [err, setErr] = useState(null)

  const { data: contactsData } = useQuery({
    queryKey: ['contacts'],
    queryFn: () => api.get('/api/contacts').then(r => r.data.data ?? []),
  })
  const allContacts = contactsData ?? []

  const qc = useQueryClient()

  function set(k, v) { setForm(p => ({ ...p, [k]: v })); setErr(null) }

  async function handleSubmit(e) {
    e.preventDefault()
    if (!form.type)   return setErr('Type is required.')
    if (!form.date)   return setErr('Date is required.')
    setSub(true); setErr(null)

    const payload = {
      type: form.type, direction: form.direction || undefined,
      source: form.source, date: new Date(form.date).toISOString(),
      mandate_id: mandateId,
    }
    if (form.subject_title) payload.subject_title = form.subject_title
    if (form.body_notes)    payload.body_notes    = form.body_notes
    if (form.follow_up_due) payload.follow_up_due = form.follow_up_due

    try {
      const res = await api.post('/api/interactions', payload)
      const newId = res.data.data?.interaction_id

      // Link each selected contact
      if (newId && form.contact_ids.length > 0) {
        await Promise.all(
          form.contact_ids.map(cid =>
            api.post(`/api/interactions/${newId}/contacts/${cid}`)
          )
        )
      }

      qc.invalidateQueries({ queryKey: ['interactions', { mandate_id: mandateId }] })
      onCreated?.()
      onClose()
    } catch (e) {
      setErr(e.response?.data?.error || e.message)
    } finally {
      setSub(false)
    }
  }

  return (
    <Modal title="Log Interaction" onClose={onClose} width={540} zIndex={zIndex}>
      <form onSubmit={handleSubmit} style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>
        <ErrorBanner msg={err} />

        {/* Type + Direction */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div className="input-group">
            <label className="input-label">Type *</label>
            <select className="select" value={form.type} onChange={e => set('type', e.target.value)}>
              {INT_TYPES.map(t => <option key={t}>{t}</option>)}
            </select>
          </div>
          <div className="input-group">
            <label className="input-label">Direction</label>
            <select className="select" value={form.direction} onChange={e => set('direction', e.target.value)}>
              <option value="">None</option>
              {DIRECTIONS.map(d => <option key={d}>{d}</option>)}
            </select>
          </div>
        </div>

        {/* Subject */}
        <div className="input-group">
          <label className="input-label">Subject / Title</label>
          <input className="input" value={form.subject_title} placeholder="e.g. Intro call with LP team"
            onChange={e => set('subject_title', e.target.value)} />
        </div>

        {/* Notes */}
        <div className="input-group">
          <label className="input-label">Notes</label>
          <textarea className="textarea" rows={3} value={form.body_notes}
            placeholder="Key discussion points, next steps…"
            onChange={e => set('body_notes', e.target.value)} />
        </div>

        {/* Date + Source */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div className="input-group">
            <label className="input-label">Date *</label>
            <input className="input" type="datetime-local" value={form.date}
              onChange={e => set('date', e.target.value)} />
          </div>
          <div className="input-group">
            <label className="input-label">Source</label>
            <select className="select" value={form.source} onChange={e => set('source', e.target.value)}>
              {INT_SOURCES.map(s => <option key={s}>{s}</option>)}
            </select>
          </div>
        </div>

        {/* Follow-up due */}
        <div className="input-group">
          <label className="input-label">Follow-up Due</label>
          <input className="input" type="date" value={form.follow_up_due}
            onChange={e => set('follow_up_due', e.target.value)} />
        </div>

        {/* Contacts multi-select */}
        <div className="input-group">
          <label className="input-label">
            Contacts{form.contact_ids.length > 0 && ` (${form.contact_ids.length} selected)`}
          </label>
          <EntityMultiField kind="contact" records={allContacts} value={form.contact_ids}
            onChange={ids => set('contact_ids', ids)} />
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, paddingTop: 4,
          borderTop: '1px solid var(--border)', marginTop: 4 }}>
          <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={submitting}>
            {submitting ? 'Logging…' : 'Log Interaction'}
          </button>
        </div>
      </form>
    </Modal>
  )
}

// ── Activity Feed ─────────────────────────────────────────────────────────

const ACTIVITY_PAGE_SIZE = 15

// Subjects render in full now that the feed is full-width — only a genuinely
// long one gets clipped, rather than every row being cut at one line.
function clipSubject(s, max = 180) {
  if (!s) return '(no subject)'
  return s.length > max ? s.slice(0, max - 1) + '…' : s
}

const namesOf = (links, key) => (links ?? []).map(l => l[key]?.name).filter(Boolean)

function ActivityFeed({ mandateId }) {
  const [logOpen, setLogOpen] = useState(false)
  const [openId, setOpenId]   = useState(null)
  const qc = useQueryClient()
  const queryKey = ['interactions', { mandate_id: mandateId }]

  const {
    data, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage,
  } = useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam }) => api.get('/api/interactions', {
      params: { mandate_id: mandateId, limit: ACTIVITY_PAGE_SIZE, offset: pageParam },
    }).then(r => ({ rows: r.data.data ?? [], total: r.data.meta?.total ?? 0 })),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((n, p) => n + p.rows.length, 0)
      return loaded < lastPage.total ? loaded : undefined
    },
    enabled: !!mandateId,
  })

  const interactions = data?.pages.flatMap(p => p.rows) ?? []
  const total = data?.pages[0]?.total ?? 0

  return (
    <div className="card" style={{ marginBottom: 20 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <div>
          <h2 style={{ fontSize: 14, fontWeight: 600 }}>Activity Feed</h2>
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>
            {isLoading ? '…' : `${total} interaction${total !== 1 ? 's' : ''}`}
          </div>
        </div>
        <button className="btn btn-secondary btn-sm" onClick={() => setLogOpen(true)}>
          + Log
        </button>
      </div>

      {/* List */}
      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8 }}>
        {isLoading ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 40 }}>
            <div className="spinner" />
          </div>
        ) : interactions.length === 0 ? (
          <div className="empty-state" style={{ padding: '32px 20px' }}>
            <h3 style={{ fontSize: 13 }}>No interactions yet</h3>
            <p>Log the first touchpoint for this mandate.</p>
          </div>
        ) : (
          interactions.map((i, idx) => {
            const contactNames = namesOf(i.interaction_contacts, 'contact')
            const firmNames    = namesOf(i.interaction_firms, 'firm')
            return (
              <div key={i.interaction_id}
                className="activity-feed-row"
                onClick={() => setOpenId(i.interaction_id)}
                style={{
                  padding: '12px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 16,
                  borderBottom: idx < interactions.length - 1 ? '1px solid var(--border)' : 'none',
                }}
              >
                <span className={`badge ${TYPE_BADGE[i.type] ?? 'badge-gray'}`} style={{ flexShrink: 0 }}>
                  {i.type}
                </span>
                <div style={{ flex: '2 1 260px', minWidth: 0, fontSize: 13, fontWeight: 500,
                  color: 'var(--text-primary)', overflowWrap: 'anywhere' }}>
                  {clipSubject(i.subject_title)}
                </div>
                <div style={{ flex: '1 1 160px', minWidth: 0, fontSize: 12, color: 'var(--text-secondary)',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {contactNames.join(', ') || '—'}
                </div>
                <div style={{ flex: '1 1 140px', minWidth: 0, fontSize: 12, color: 'var(--text-secondary)',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {firmNames.join(', ') || '—'}
                </div>
                {i.direction && (
                  <span className="badge badge-gray" style={{ fontSize: 10, flexShrink: 0 }}>{i.direction}</span>
                )}
                <span style={{ fontSize: 11, color: 'var(--text-muted)', flexShrink: 0, width: 108, textAlign: 'right' }}>
                  {formatDateTime(i.date)}
                </span>
              </div>
            )
          })
        )}
      </div>

      {hasNextPage && (
        <div style={{ textAlign: 'center', marginTop: 12 }}>
          <button className="btn btn-secondary btn-sm" onClick={() => fetchNextPage()} disabled={isFetchingNextPage}>
            {isFetchingNextPage ? 'Loading…' : 'Show more'}
          </button>
        </div>
      )}

      {logOpen && (
        <LogInteractionModal
          mandateId={mandateId}
          onClose={() => setLogOpen(false)}
          onCreated={() => qc.invalidateQueries({ queryKey })}
        />
      )}

      {openId && (
        <InteractionDrawer
          interactionId={openId}
          onClose={() => setOpenId(null)}
          onChanged={() => qc.invalidateQueries({ queryKey })}
        />
      )}
    </div>
  )
}

// ── Interaction Drawer ───────────────────────────────────────────────────────
// Right-side panel with the full record: body, recipients, attachments,
// linked records, and (for manually logged interactions) editable notes and
// contact linking. Closes on the X, Esc, or a click outside.

function gmailLink(interaction) {
  if (!interaction.thread_id && !interaction.gmail_message_id) return null
  const frag = interaction.thread_id || interaction.gmail_message_id
  const authuser = interaction.mailbox_owner ? `?authuser=${encodeURIComponent(interaction.mailbox_owner)}` : ''
  return `https://mail.google.com/mail/${authuser}#all/${frag}`
}

function InteractionDrawer({ interactionId, onClose, onChanged }) {
  const qc = useQueryClient()
  const queryKey = ['interaction', interactionId]

  const { data: interaction, isLoading } = useQuery({
    queryKey,
    queryFn: () => api.get(`/api/interactions/${interactionId}`).then(r => r.data.data),
    enabled: !!interactionId,
  })

  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  async function downloadAttachment(att) {
    const res = await api.get(`/api/interactions/${interactionId}/attachments/${att.gmail_attachment_id}`, {
      responseType: 'blob',
    })
    const url = URL.createObjectURL(res.data)
    const a = document.createElement('a')
    a.href = url
    a.download = att.filename || 'attachment'
    a.click()
    URL.revokeObjectURL(url)
  }

  const refresh = () => { qc.invalidateQueries({ queryKey }); onChanged?.() }

  return (
    <div onMouseDown={e => e.target === e.currentTarget && onClose()} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1200,
      display: 'flex', justifyContent: 'flex-end',
    }}>
      <div className="drawer-panel" style={{
        width: '100%', maxWidth: 580, height: '100%', background: 'var(--surface)',
        borderLeft: '1px solid var(--border-light)', boxShadow: '-20px 0 60px rgba(0,0,0,0.6)',
        display: 'flex', flexDirection: 'column',
      }}>
        <div style={{
          padding: '14px 20px', borderBottom: '1px solid var(--border)',
          display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0,
        }}>
          <h2 style={{ fontSize: 15, fontWeight: 600, flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>
            {interaction?.subject_title || (isLoading ? '…' : '(no subject)')}
          </h2>
          <button className="btn-icon" onClick={onClose} aria-label="Close" style={{ fontSize: 16 }}>✕</button>
        </div>

        <div style={{ padding: '18px 22px', overflowY: 'auto', flex: 1 }}>
          {isLoading || !interaction ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 30 }}><div className="spinner" /></div>
          ) : (
            <InteractionDrawerBody interaction={interaction} onChanged={refresh} onDownload={downloadAttachment} />
          )}
        </div>
      </div>
    </div>
  )
}

function InteractionDrawerBody({ interaction: i, onChanged, onDownload }) {
  const [notes, setNotes]     = useState(i.body_notes || '')
  const [saving, setSaving]   = useState(false)
  const [saved, setSaved]     = useState(false)
  const gmailUrl = gmailLink(i)

  async function saveNotes() {
    setSaving(true)
    try {
      await api.patch(`/api/interactions/${i.interaction_id}`, { body_notes: notes })
      onChanged()
      setSaved(true)
      setTimeout(() => setSaved(false), 2000)
    } finally {
      setSaving(false)
    }
  }

  const linkedContactIds = (i.interaction_contacts ?? []).map(ic => ic.contact?.contact_id).filter(Boolean)

  async function changeContacts(newIds) {
    const removed = linkedContactIds.filter(id => !newIds.includes(id))
    const added   = newIds.filter(id => !linkedContactIds.includes(id))
    await Promise.all([
      ...removed.map(id => api.delete(`/api/interactions/${i.interaction_id}/contacts/${id}`)),
      ...added.map(id => api.post(`/api/interactions/${i.interaction_id}/contacts/${id}`)),
    ])
    onChanged()
  }

  const row = (label, value) => value != null && value !== '' && (
    <div style={{ display: 'grid', gridTemplateColumns: '70px 1fr', gap: 10, fontSize: 13, marginBottom: 6 }}>
      <div style={{ color: 'var(--text-muted)' }}>{label}</div>
      <div style={{ minWidth: 0, overflowWrap: 'anywhere' }}>{value}</div>
    </div>
  )

  return (
    <>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 14, flexWrap: 'wrap' }}>
        <span className={`badge ${TYPE_BADGE[i.type] ?? 'badge-gray'}`}>{i.type}</span>
        {i.direction && <span className="badge badge-gray">{i.direction}</span>}
        <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{formatDateTime(i.date)}</span>
        {gmailUrl && (
          <a href={gmailUrl} target="_blank" rel="noreferrer" className="btn btn-secondary btn-sm" style={{ marginLeft: 'auto' }}>
            Open in Gmail ↗
          </a>
        )}
      </div>

      {row('From', i.sender_email)}
      {row('To', (i.to_emails ?? []).join(', '))}
      {row('CC', (i.cc_emails ?? []).join(', '))}

      <div className="divider" style={{ margin: '14px 0' }} />

      {/* Body */}
      {i.body_html ? (
        <div
          style={{ fontSize: 13, lineHeight: 1.5, overflowWrap: 'anywhere' }}
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(i.body_html, { ADD_ATTR: ['target'] }) }}
        />
      ) : i.body_full_text || i.body_notes ? (
        <div style={{ fontSize: 13, lineHeight: 1.5, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
          {i.body_full_text || i.body_notes}
        </div>
      ) : i.source === 'Gmail Sync' ? (
        <div style={{ fontSize: 12, color: 'var(--text-muted)', fontStyle: 'italic' }}>
          Full body not available for this message yet.
        </div>
      ) : null}

      {/* Manual interactions: editable notes */}
      {i.source === 'Manual' && (
        <div style={{ marginTop: 14 }}>
          <label className="input-label">Notes</label>
          <textarea className="textarea" rows={5} value={notes} onChange={e => setNotes(e.target.value)} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 6 }}>
            <button className="btn btn-secondary btn-sm" onClick={saveNotes} disabled={saving}>
              {saving ? 'Saving…' : 'Save notes'}
            </button>
            {saved && <span style={{ fontSize: 12, color: 'var(--success)' }}>Saved</span>}
          </div>
        </div>
      )}

      {/* Attachments */}
      {(i.interaction_attachments ?? []).length > 0 && (
        <Section title={`Attachments (${i.interaction_attachments.length})`}>
          {i.interaction_attachments.map(att => (
            <div key={att.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, marginBottom: 4 }}>
              <button type="button" className="btn btn-ghost btn-sm" style={{ padding: '2px 6px' }}
                onClick={() => onDownload(att)}>
                ⬇ {att.filename || 'Attachment'}
              </button>
              {att.size_bytes != null && (
                <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{formatFileSize(att.size_bytes)}</span>
              )}
            </div>
          ))}
        </Section>
      )}

      {/* Linked records */}
      <Section title="Linked contacts">
        {i.source === 'Manual' ? (
          <EntityMultiField kind="contact" value={linkedContactIds} onChange={changeContacts} />
        ) : (i.interaction_contacts ?? []).length > 0 ? (
          (i.interaction_contacts ?? []).map(ic => ic.contact && (
            <div key={ic.contact.contact_id} style={{ fontSize: 13, marginBottom: 4 }}>
              <PeekLink type="contact" id={ic.contact.contact_id}>{ic.contact.name}</PeekLink>
              {ic.contact.title_role && <span style={{ color: 'var(--text-muted)', fontSize: 12 }}> · {ic.contact.title_role}</span>}
            </div>
          ))
        ) : (
          <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>None linked.</div>
        )}
      </Section>

      {(i.interaction_firms ?? []).length > 0 && (
        <Section title="Linked firms">
          {i.interaction_firms.map(ifm => ifm.firm && (
            <div key={ifm.firm.firm_id} style={{ fontSize: 13, marginBottom: 4 }}>
              <PeekLink type="firm" id={ifm.firm.firm_id}>{ifm.firm.name}</PeekLink>
            </div>
          ))}
        </Section>
      )}

      {i.pipeline && (
        <Section title="Pipeline investor">
          <div style={{ fontSize: 13 }}>
            {i.pipeline.investor_firm ? (
              <PeekLink type="firm" id={i.pipeline.investor_firm.firm_id}>{i.pipeline.investor_firm.name}</PeekLink>
            ) : '—'}
            {i.pipeline.stage && <span className={`badge ${PL_BADGE[i.pipeline.stage] ?? 'badge-gray'}`} style={{ marginLeft: 8, fontSize: 10 }}>{i.pipeline.stage}</span>}
          </div>
        </Section>
      )}
    </>
  )
}

function Section({ title, children }) {
  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em',
        color: 'var(--text-secondary)', marginBottom: 6 }}>{title}</div>
      {children}
    </div>
  )
}

// ── Main page ─────────────────────────────────────────────────────────────

export default function MandateDetail() {
  const { id } = useParams()
  const location = useLocation()

  const { data: mandateData, isLoading: mandateLoading, error: mandateError } = useQuery({
    queryKey: ['mandate', id],
    queryFn: () => api.get(`/api/mandates/${id}`).then(r => r.data.data),
    enabled: !!id,
  })

  const { data: pipelineData, isLoading: pipelineLoading } = useQuery({
    queryKey: ['pipeline', id],
    queryFn: () => api.get(`/api/mandates/${id}/pipeline`).then(r => r.data.data ?? []),
    enabled: !!id,
  })

  const { data: usersData } = useQuery({
    queryKey: ['users'],
    queryFn: () => api.get('/api/users').then(r => r.data.data ?? []),
  })

  // Back goes to wherever the mandate was opened from: links on the
  // Dashboard pass { from: 'dashboard' } in router state; anything else
  // (pipeline, search, direct URL) goes back to the pipeline.
  const back = location.state?.from === 'dashboard'
    ? { to: '/', label: '← Dashboard' }
    : { to: '/mandates', label: '← Mandate Pipeline' }

  const mandate     = mandateData     ?? null
  const pipeline    = pipelineData    ?? []

  // ── Loading ──────────────────────────────────────────────────────────
  if (mandateLoading) {
    return (
      <div className="page">
        <Link to={back.to} style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
          {back.label}
        </Link>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 300 }}>
          <div className="spinner spinner-lg" />
        </div>
      </div>
    )
  }

  // ── Error / not found ────────────────────────────────────────────────
  if (mandateError || !mandate) {
    return (
      <div className="page">
        <Link to={back.to} style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
          {back.label}
        </Link>
        <div style={{
          marginTop: 24, background: 'var(--danger-muted)',
          border: '1px solid rgba(239,68,68,0.25)',
          borderRadius: 8, padding: '20px 24px', color: 'var(--danger-text)', fontSize: 14,
        }}>
          {mandateError?.response?.status === 404
            ? 'Mandate not found.'
            : `Error: ${mandateError?.message ?? 'Could not load mandate.'}`}
        </div>
      </div>
    )
  }

  return (
    <div className="page">

      {/* Back link */}
      <div style={{ marginBottom: 16 }}>
        <Link to={back.to} style={{
          fontSize: 13, color: 'var(--text-secondary)',
          display: 'inline-flex', alignItems: 'center', gap: 4,
        }}>
          {back.label}
        </Link>
      </div>

      {/* ── Section 1: Mandate header ──────────────────────────────── */}
      <MandateHeader mandate={mandate} />

      {/* ── Action items for this mandate, every assignee ─────────── */}
      <div className="card" style={{ marginBottom: 20 }}>
        <h2 style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>Action Items</h2>
        <ActionItemsList filter={{ mandate_id: id }} defaultMandateId={id}
          emptyText="No open action items for this mandate." />
      </div>

      {/* ── Section 2: Documents ───────────────────────────────────── */}
      {/* ── Partners being worked on this mandate ─────────────────── */}
      <PartnerPipelineSection mandateId={id} />

      {/* ── Names partners submitted for this mandate, every status ── */}
      <SubmittedNamesSection mandateId={id} />

      {/* ── Strategic / business development names on this mandate ───── */}
      <NamePipelineSection mandateId={id} />

      {/* ── Partner materials: what engaged partners see (migrations/079) ── */}
      {/* ── and the engagement letters, RGA only ─────────────────────── */}
      <div className="card" style={{ marginBottom: 20 }}>
        <h2 style={{ fontSize: 14, fontWeight: 600 }}>Materials</h2>
        <div style={{ fontSize: 12, color: 'var(--text-secondary)', margin: '2px 0 14px' }}>
          Partner materials are shown to partners on this mandate. Engagement letters are RGA only.
        </div>
        <MaterialsSection mandateId={id} mode="crm" />
        <div style={{ borderTop: '1px solid var(--border)', marginTop: 20, paddingTop: 16 }}>
          <EngagementLetters mandateId={id} />
        </div>
      </div>

      <DocumentsSection mandateId={id} />

      {/* ── Section 3: Investor pipeline, full width ──────────────── */}
      <div style={{ marginBottom: 20 }}>
        {pipelineLoading ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 200 }}>
            <div className="spinner" />
          </div>
        ) : (
          <PipelineKanban
            entries={pipeline}
            mandateId={id}
            mandate={mandate}
          />
        )}
      </div>

      {/* ── Section 4: Activity feed, full width, below the pipeline ──── */}
      <ActivityFeed mandateId={id} />
    </div>
  )
}
